/**
 * Mock Data Service
 * Provides dummy data for testing mode when TESTING_DATA=true
 */

// Import existing dummy data files
import adminsData from '../../data/admins.json';
import usersData from '../../data/users.json';
import rawTransactionsData from '../../data/transactions.json';
import adminActivityData from '../../data/adminActivity.json';
import { storage } from '../utils/storage';

// Helper to check if we're in development mode
const isDevelopment = () => process.env.NEXT_PUBLIC_ENV === 'development';

// Enhanced transactions data with additional fields
const transactionsData = rawTransactionsData.map((tx, index) => ({
  ...tx,
  currency: Math.random() > 0.8 ? (Math.random() > 0.5 ? 'GHS' : 'USD') : 'RWF',
  user_type: Math.random() > 0.2 ? 'user' : 'agent',
  sender_id: `USER-${Math.floor(Math.random() * 10000)}`,
  receiver_id: `USER-${Math.floor(Math.random() * 10000)}`,
  transaction_fee: Math.floor(tx.amount * 0.02),
  created_at: new Date(tx.date).toISOString(),
  reference: `REF-${tx.id}-${Date.now()}`,
  description: `${tx.type} transaction`,
  ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
  platform: Math.random() > 0.5 ? 'mobile' : 'web'
}));

// Helper function to simulate API response delay
const simulateDelay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Helper functions for enhanced user data generation
const generateRandomDate = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const randomTime = startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime());
  return new Date(randomTime).toISOString().split('T')[0];
};

const getRandomCountry = () => {
  const countries = ['Rwanda', 'Ghana', 'Nigeria', 'Kenya', 'Uganda', 'Tanzania', 'Ethiopia'];
  return countries[Math.floor(Math.random() * countries.length)];
};

const getRandomDistrict = () => {
  const districts = ['Kigali', 'Gasabo', 'Kicukiro', 'Nyarugenge', 'Musanze', 'Huye', 'Rubavu'];
  return districts[Math.floor(Math.random() * districts.length)];
};

const generateRandomAddress = () => {
  const streets = ['KG Ave', 'Nyarutarama Rd', 'Kimisagara St', 'Gisimenti Ave', 'Kibagabaga Rd'];
  const numbers = Math.floor(Math.random() * 999) + 1;
  return `${numbers} ${streets[Math.floor(Math.random() * streets.length)]}`;
};

const generateAccountNumber = () => {
  return Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join('');
};

// Generate comprehensive user details
const generateUserDetails = (user) => {
  const isAgent = user.userType === 'agent';
  const isVerified = Math.random() > 0.3; // 70% chance of being verified
  const hasAgentApplication = !isAgent && Math.random() > 0.8; // 20% chance of pending agent application

  // Generate dynamic wallets based on user preferences
  const availableCurrencies = ['RWF', 'GHS', 'USD', 'EUR', 'KES'];
  const userCurrencies = [];
  const numCurrencies = Math.floor(Math.random() * 3) + 1; // 1-3 currencies
  
  for (let i = 0; i < numCurrencies; i++) {
    const currency = availableCurrencies[i % availableCurrencies.length];
    if (!userCurrencies.includes(currency)) {
      userCurrencies.push(currency);
    }
  }

  // Generate user wallet balances
  const userWalletBalances = {};
  userCurrencies.forEach(currency => {
    userWalletBalances[currency] = Math.floor(Math.random() * 500000) + 10000;
  });

  // Generate agent wallet balances (if user is agent)
  const agentWalletBalances = {};
  const agentCommissionBalances = {};
  if (isAgent) {
    userCurrencies.forEach(currency => {
      agentWalletBalances[currency] = Math.floor(Math.random() * 1000000) + 50000;
      agentCommissionBalances[currency] = Math.floor(Math.random() * 10000) + 500;
    });
  }

  // Generate legacy wallet structure for backward compatibility
  const legacyWallets = userCurrencies.map((currency, index) => {
    const countryCodes = {
      'RWF': 'RW',
      'GHS': 'GH',
      'USD': 'US',
      'EUR': 'EU',
      'KES': 'KE'
    };
    
  return {
      currency: currency,
      balance: userWalletBalances[currency],
      country_code: countryCodes[currency]
    };
  });

  // Calculate combined balances
  const combinedBalances = {};
  userCurrencies.forEach(currency => {
    const userBalance = userWalletBalances[currency] || 0;
    const agentBalance = agentWalletBalances[currency] || 0;
    combinedBalances[currency] = userBalance + agentBalance;
  });

  return {
    id: user.userId,
    avatar: user.avatar,
    full_name: user.fullName,
    email: `${user.fullName.toLowerCase().replace(/\s+/g, '.')}@example.com`,
    phone_number: user.phoneNumber,
    gender: Math.random() > 0.5 ? 'male' : 'female',
    date_of_birth: generateRandomDate('1980-01-01', '2005-12-31'),
    country_of_birth: getRandomCountry(),
    primary_citizenship: getRandomCountry(),
    sending_money_from: getRandomCountry(),
    date_joined: user.dateCreated,
    is_verified: isVerified,
    is_active: Math.random() > 0.1,
    is_agent: isAgent,
    agent_application_pending: hasAgentApplication,
    user_type: user.userType,
    status: Math.random() > 0.1 ? 'active' : 'suspended',
    residential_address: {
      street: generateRandomAddress(),
      district: getRandomDistrict(),
      country: getRandomCountry()
    },

    // Enhanced wallet summary structure
    wallet_summary: {
      user_wallets: [
        {
          id: `550e8400-e29b-41d4-a716-${user.userId}`,
          wallet_number: generateAccountNumber(),
          status: 'active',
          balances: userWalletBalances,
          default_currency: userCurrencies[0],
          daily_limit: 100000.0,
          monthly_limit: 500000.0,
          created_at: generateRandomDate('2024-01-01', '2025-01-15')
        }
      ],
      agent_wallets: isAgent ? [
        {
          id: `550e8400-e29b-41d4-a717-${user.userId}`,
          wallet_number: generateAccountNumber(),
          status: 'active',
          balances: agentWalletBalances,
          commission_balances: agentCommissionBalances,
          float_balance: Math.floor(Math.random() * 10000) + 500,
          tier: Math.random() > 0.5 ? 'premium' : 'standard',
          default_currency: userCurrencies[0],
          created_at: generateRandomDate('2024-01-01', '2025-01-15')
        }
      ] : [],
      total_currencies: userCurrencies,
      combined_balances: combinedBalances
    },

    // Legacy fields for backward compatibility
    account_balance: {
      amount: userWalletBalances[userCurrencies[0]] || 0,
      currency: userCurrencies[0] || 'RWF',
      account_number: generateAccountNumber()
    },
    agent_balance: isAgent ? {
      amount: agentWalletBalances[userCurrencies[0]] || 0,
      currency: userCurrencies[0] || 'RWF',
      status: 'active',
      country: getRandomCountry()
    } : null,
    wallets: legacyWallets,

    has_agent_application: hasAgentApplication,
    agent_application_id: hasAgentApplication ? `APP-${Date.now()}` : null,
    agent_application_status: hasAgentApplication ? 'pending' : null
  };
};

// Mock Data Service
export const mockDataService = {
  // Authentication endpoints
  auth: {
    login: async (credentials) => {
      await simulateDelay(800);
      if (isDevelopment()) console.log('🧪 Testing Mode: Login attempt', { email: credentials.email });

      return {
        access_token: 'mock_access_token_' + Date.now(),
        refresh_token: 'mock_refresh_token_' + Date.now(),
        user: {
          id: 1,
          email: credentials.email,
          name: 'Test Admin',
          role: 'admin'
        }
      };
    },

    // Admin authentication functions
    adminLogin: async (credentials) => {
      await simulateDelay(800);
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin login attempt', { email: credentials.email });

      // Simulate different account statuses for testing
      // 20% chance of PENDING account (to test the new flow)
      const isPendingAccount = credentials.email.includes('pending') || Math.random() < 0.2;
      
      if (isPendingAccount) {
        if (isDevelopment()) console.log('🧪 Testing Mode: Simulating PENDING account status error');
        // Simulate the backend error for PENDING accounts
        const error = new Error('admin account is not active');
        error.status = 403;
        error.response = { detail: 'admin account is not active', account_status: 'PENDING' };
        throw error;
      }
      
      if (isDevelopment()) console.log('🧪 Testing Mode: Login successful - Password change required (consistent flow)');

      return {
        access_token: 'mock_admin_access_token_' + Date.now(),
        refresh_token: 'mock_admin_refresh_token_' + Date.now(),
        user: {
          id: 1,
          email: credentials.email,
          name: 'Test Admin',
          role: 'admin',
          status: 'ACTIVE', // Regular active account
          permissions: ['read', 'write', 'admin']
        },
        message: 'Login successful - Password change required',
        password_change_required: true // Always require password change in testing mode
      };
    },

    adminLoginOtp: async (email) => {
      await simulateDelay(600);
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin OTP request', { email });
      if (isDevelopment()) console.log('🧪 Testing Mode: OTP sent (you can use any 4+ digit code)');

      return {
        success: true,
        message: 'OTP sent successfully',
        otp_sent: true,
        email: email
      };
    },

    adminVerifyOtp: async (otpData) => {
      await simulateDelay(700);
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin OTP verification', { email: otpData.email, otp: otpData.otp });

      if (otpData.otp && otpData.otp.length >= 4) {
        if (isDevelopment()) console.log('🧪 Testing Mode: OTP verified - Authentication complete! Redirecting to dashboard...');
        return {
          access_token: 'mock_admin_access_token_' + Date.now(),
          refresh_token: 'mock_admin_refresh_token_' + Date.now(),
          user: {
            id: 1,
            email: otpData.email,
            name: 'Test Admin',
            role: 'admin',
            permissions: ['read', 'write', 'admin']
          },
          success: true,
          message: 'OTP verified successfully'
        };
      }
      throw new Error('Invalid OTP code');
    },

    adminChangePassword: async (passwordData) => {
      await simulateDelay(700);
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin password change', passwordData);
      
      // Check if this is a PENDING account activation
      const tempSession = JSON.parse(storage.getItem('temp_admin_session') || '{}');
      const isPendingAccountActivation = tempSession.account_status === 'PENDING';
      
      if (isPendingAccountActivation) {
        if (isDevelopment()) console.log('🧪 Testing Mode: PENDING account activated through password change');
        if (isDevelopment()) console.log('🧪 Testing Mode: Account status changed from PENDING to ACTIVE');
        
        // Update temp session to reflect activation
        tempSession.account_status = 'ACTIVE';
        tempSession.user.status = 'ACTIVE';
        storage.setItem('temp_admin_session', JSON.stringify(tempSession));
      }
      
      if (isDevelopment()) console.log('🧪 Testing Mode: Password changed - Moving to OTP step');

      return {
        success: true,
        message: isPendingAccountActivation 
          ? 'Account activated and password changed successfully - OTP required' 
          : 'Password changed successfully - OTP required',
        otp_required: true, // Always require OTP in testing mode
        requires_otp: true, // Alternative field name for compatibility
        next_step: 'otp_verification',
        account_activated: isPendingAccountActivation,
        email: passwordData.email || tempSession.email || 'test@admin.com'
      };
    },

    verifyOTP: async (otpData) => {
      await simulateDelay(600);
      if (isDevelopment()) console.log('🧪 Testing Mode: OTP verification', { email: otpData.email, otp: otpData.otp });

      if (otpData.otp && otpData.otp.length === 6) {
        return {
          success: true,
          message: 'OTP verified successfully',
          redirect_url: '/dashboard'
        };
      }
      throw new Error('Invalid OTP');
    },

    changePassword: async (passwordData) => {
      await simulateDelay(700);
      if (isDevelopment()) console.log('🧪 Testing Mode: Password change', { email: passwordData.email });

      return {
        success: true,
        message: 'Password changed successfully',
        otp_required: true
      };
    },

    createPassword: async (passwordData) => {
      await simulateDelay(500);
      if (isDevelopment()) console.log('🧪 Testing Mode: Password creation', { email: passwordData.email });

      return {
        success: true,
        message: 'Password created successfully'
      };
    },

    verifyAccount: async (verificationData) => {
      await simulateDelay(600);
      if (isDevelopment()) console.log('🧪 Testing Mode: Account verification', verificationData);

      return {
        success: true,
        message: 'Account verified successfully'
      };
    },

    validateToken: async (token) => {
      await simulateDelay(200);
      return { valid: true, user: { id: 1, email: 'test@example.com' } };
    },

    refreshAccessToken: async () => {
      await simulateDelay(300);
      return { access_token: 'new_mock_access_token_' + Date.now() };
    }
  },

  // Dashboard endpoints
  dashboard: {
    overview: async (period = 'monthly', metric = null) => {
      await simulateDelay();

      if (isDevelopment()) console.log('🧪 Testing Mode: Dashboard overview requested', { period, metric });

      // Mock dashboard data with period variations
      const baseData = {
        total_revenue: {
          current: 2456789.50,
          currency: 'RWF',
          trend: 'increase',
          percentage_change: 12.5,
          period: period
        },
        transaction_volume: {
          current: 156892,
          trend: 'increase',
          percentage_change: 8.3,
          period: period
        },
        active_users: {
          current: 18965,
          trend: 'increase',
          percentage_change: 5.2,
          period: period
        }
      };

      if (metric && baseData[metric]) {
        return { [metric]: baseData[metric] };
      }

      return baseData;
    },

    users: async (params = {}) => {
      await simulateDelay();
      const { page = 1, limit = 20, search = '' } = params;
      let filteredUsers = usersData;

      if (search) {
        filteredUsers = usersData.filter(user =>
          user.fullName.toLowerCase().includes(search.toLowerCase()) ||
          user.userId.toLowerCase().includes(search.toLowerCase()) ||
          user.phoneNumber.toLowerCase().includes(search.toLowerCase())
        );
      }

      const totalItems = filteredUsers.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);

      // Transform users data to match API response structure
      const apiUsers = filteredUsers.slice(start, end).map(user => ({
        user_id: user.userId,
        full_name: user.fullName,
        phone_number: user.phoneNumber,
        user_type: user.userType,
        date_created: user.dateCreated,
        time_created: user.timeCreated,
        avatar: user.avatar,
        // Add agent application fields
        has_agent_application: Math.random() > 0.8, // 20% chance
        agent_application_id: Math.random() > 0.8 ? `APP-${Date.now()}-${Math.floor(Math.random() * 1000)}` : null,
        agent_application_status: Math.random() > 0.8 ? (Math.random() > 0.5 ? 'pending' : 'approved') : null
      }));

      return {
        users: apiUsers,
        total: totalItems,
        total_items: totalItems,
        page: validPage,
        limit: parseInt(limit),
        total_pages: totalPages,
        pagination: {
          total: totalItems,
          total_items: totalItems,
          page: validPage,
          limit: parseInt(limit),
          total_pages: totalPages,
          current_page: validPage,
          items_per_page: parseInt(limit),
          has_next: validPage < totalPages,
          has_prev: validPage > 1
        }
      };
    },

    userDetails: async (userId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: User details requested for ID:', userId);

      const baseUser = usersData.find(u => u.userId === userId || u.id === parseInt(userId));
      if (!baseUser) {
        throw new Error(`User not found: ${userId}`);
      }

      return generateUserDetails(baseUser);
    },

    userDistribution: async () => {
      await simulateDelay();
      return {
        RWA: 184345,
        GHA: 15230,
        ETH: 8500,
        TZA: 3400,
        UGA: 2100,
        KEN: 1800,
        NGA: 950
      };
    },

    activities: async (limit = 10) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Activities requested with limit:', limit);

      // Transform raw adminActivity data to match component expectations
      const transformedActivities = adminActivityData.slice(0, limit).map((activity, index) => ({
        id: `${activity.activityId}-${index}`,
        activityId: activity.activityId,
        sessionId: activity.sessionId,
        adminName: 'Admin User', // Default admin name since it's not in the raw data
        action: activity.message,
        timeElapsed: activity.timestamp,
        dpImg: '/assets/images/admin_placeholder_img.png',
        message: activity.message,
        timestamp: activity.timestamp,
        associatedId: activity.associatedId
      }));

      return {
        activities: transformedActivities
      };
    },

    recentActivities: async (limit = 10) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Recent activities requested with limit:', limit);

      // Transform raw adminActivity data to match component expectations
      const transformedActivities = adminActivityData.slice(0, limit).map((activity, index) => ({
        id: `${activity.activityId}-${index}`,
        activityId: activity.activityId,
        sessionId: activity.sessionId,
        adminName: 'Admin User', // Default admin name since it's not in the raw data
        action: activity.message,
        timeElapsed: activity.timestamp,
        dpImg: '/assets/images/admin_placeholder_img.png',
        message: activity.message,
        timestamp: activity.timestamp,
        associatedId: activity.associatedId
      }));

      return {
        activities: transformedActivities
      };
    },

    transactionHeatmap: async (year = '2025') => {
      await simulateDelay();

      const data = [];
      const startDate = new Date(`${year}-01-01`);
      const endDate = new Date(`${year}-12-31`);

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const baseActivity = isWeekend ? 50 : 200;
        const count = Math.floor(baseActivity + Math.random() * 100);

        data.push({
          date: dateStr,
          count: count
        });
      }

      return {
        transaction_heatmap: data
      };
    },

    userTransactions: async (userId, params = {}) => {
      await simulateDelay();
      const { page = 1, limit = 9 } = params;

      if (isDevelopment()) console.log('🧪 Testing Mode: User transactions requested for ID:', userId);

      // Generate mock transactions for this user
      const transactionTypes = ['transfer', 'deposit', 'withdrawal', 'payment', 'refund'];
      const statuses = ['completed', 'pending', 'failed'];
      const currencies = ['RWF', 'GHS', 'USD'];

      const userTransactions = Array.from({ length: 50 }, (_, i) => ({
        id: `TXN-${Date.now()}-${i.toString().padStart(3, '0')}`,
        user_id: userId,
        type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
        amount: Math.floor(Math.random() * 100000) + 100,
        currency: currencies[Math.floor(Math.random() * currencies.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        date: generateRandomDate('2024-01-01', '2025-01-15'),
        description: `${transactionTypes[Math.floor(Math.random() * transactionTypes.length)]} transaction`,
        reference: `REF-${Date.now()}-${i}`,
        fee: Math.floor(Math.random() * 1000) + 10,
        recipient: Math.random() > 0.5 ? 'External Account' : 'Internal Transfer',
        created_at: generateRandomDate('2024-01-01', '2025-01-15')
      }));

      const totalItems = userTransactions.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);

      return {
        transactions: userTransactions.slice(start, end),
        total: totalItems,
        page: validPage,
        limit: parseInt(limit),
        total_pages: totalPages,
        pagination: {
          total: totalItems,
          total_items: totalItems,
          page: validPage,
          current_page: validPage,
          limit: parseInt(limit),
          items_per_page: parseInt(limit),
          total_pages: totalPages,
          has_next: validPage < totalPages,
          has_prev: validPage > 1
        }
      };
    },

    userTransactionBreakdown: async (userId = null, params = {}) => {
      await simulateDelay();
      const { 
        user_page = 1, 
        agent_page = 1, 
        user_limit = 9, 
        agent_limit = 9,
        ...otherParams 
      } = params;

      if (isDevelopment()) console.log('🧪 Testing Mode: User transaction breakdown requested for ID:', userId, 'with params:', params);

      // Generate mock user transactions (personal wallet)
      const userTransactionTypes = ['transfer', 'deposit', 'withdrawal', 'payment'];
      const statuses = ['completed', 'pending', 'failed'];
      const currencies = ['RWF', 'GHS', 'USD'];

      const userTransactions = Array.from({ length: 30 }, (_, i) => ({
        id: `USER-TXN-${Date.now()}-${i.toString().padStart(3, '0')}`,
        user_id: userId || 'current-user',
        type: userTransactionTypes[Math.floor(Math.random() * userTransactionTypes.length)],
        amount: Math.floor(Math.random() * 50000) + 100,
        currency: currencies[Math.floor(Math.random() * currencies.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        date: generateRandomDate('2024-01-01', '2025-01-15'),
        balance: Math.floor(Math.random() * 500000) + 10000,
        description: `Personal wallet transaction`,
        reference: `USER-REF-${Date.now()}-${i}`,
        fee: Math.floor(Math.random() * 500) + 10,
        wallet_type: 'user',
        created_at: generateRandomDate('2024-01-01', '2025-01-15')
      }));

      // Generate mock agent transactions (business wallet)
      const agentTransactionTypes = ['commission', 'cash-in', 'cash-out', 'float-management'];
      const agentTransactions = Array.from({ length: 25 }, (_, i) => ({
        id: `AGENT-TXN-${Date.now()}-${i.toString().padStart(3, '0')}`,
        user_id: userId || 'current-user',
        type: agentTransactionTypes[Math.floor(Math.random() * agentTransactionTypes.length)],
        amount: Math.floor(Math.random() * 200000) + 500,
        currency: currencies[Math.floor(Math.random() * currencies.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        date: generateRandomDate('2024-01-01', '2025-01-15'),
        balance: Math.floor(Math.random() * 2000000) + 50000,
        description: `Business wallet transaction`,
        reference: `AGENT-REF-${Date.now()}-${i}`,
        fee: Math.floor(Math.random() * 2000) + 50,
        wallet_type: 'agent',
        created_at: generateRandomDate('2024-01-01', '2025-01-15')
      }));

      // Paginate user transactions
      const userTotalItems = userTransactions.length;
      const userTotalPages = Math.max(1, Math.ceil(userTotalItems / user_limit));
      const validUserPage = Math.max(1, Math.min(user_page, userTotalPages));
      const userStart = (validUserPage - 1) * user_limit;
      const userEnd = Math.min(userStart + user_limit, userTotalItems);

      // Paginate agent transactions
      const agentTotalItems = agentTransactions.length;
      const agentTotalPages = Math.max(1, Math.ceil(agentTotalItems / agent_limit));
      const validAgentPage = Math.max(1, Math.min(agent_page, agentTotalPages));
      const agentStart = (validAgentPage - 1) * agent_limit;
      const agentEnd = Math.min(agentStart + agent_limit, agentTotalItems);

      // Enhanced summary statistics with detailed breakdowns
      const generateDetailedStats = (transactions) => {
        const total_amount = transactions.reduce((sum, tx) => sum + tx.amount, 0);
        
        // Currency breakdown
        const currencies = transactions.reduce((acc, tx) => {
          acc[tx.currency] = (acc[tx.currency] || 0) + tx.amount;
          return acc;
        }, {});
        
        // Status breakdown
        const by_status = transactions.reduce((acc, tx) => {
          acc[tx.status] = (acc[tx.status] || 0) + 1;
          return acc;
        }, {});
        
        // Type breakdown
        const by_type = transactions.reduce((acc, tx) => {
          acc[tx.type] = (acc[tx.type] || 0) + 1;
          return acc;
        }, {});
        
        return {
          total_transactions: transactions.length,
          total_amount,
          currencies,
          by_status,
          by_type
        };
      };

      const userStats = generateDetailedStats(userTransactions);
      const agentStats = generateDetailedStats(agentTransactions);
      
      // Combined statistics
      const allTransactions = [...userTransactions, ...agentTransactions];
      const totalStats = generateDetailedStats(allTransactions);

      // Generate mock user info
      const mockUserInfo = {
        id: userId || 'current-user',
        full_name: ['John Uwimana', 'Sarah Mukamana', 'Peter Nshimiyimana', 'Grace Uwimana', 'David Niyonzima'][Math.floor(Math.random() * 5)],
        email: `user${Math.floor(Math.random() * 1000)}@example.com`,
        role: Math.random() > 0.7 ? 'agent' : 'user',
        status: Math.random() > 0.1 ? 'active' : 'inactive'
      };

      return {
        success: true,
        user_transactions: userTransactions.slice(userStart, userEnd),
        agent_transactions: agentTransactions.slice(agentStart, agentEnd),
        summary: {
          user: userStats,
          agent: agentStats,
          total: totalStats
        },
        user_pagination: {
          total: userTotalItems,
          total_items: userTotalItems,
          page: validUserPage,
          current_page: validUserPage,
          limit: parseInt(user_limit),
          items_per_page: parseInt(user_limit),
          total_pages: userTotalPages,
          has_next: validUserPage < userTotalPages,
          has_prev: validUserPage > 1
        },
        agent_pagination: {
          total: agentTotalItems,
          total_items: agentTotalItems,
          page: validAgentPage,
          current_page: validAgentPage,
          limit: parseInt(agent_limit),
          items_per_page: parseInt(agent_limit),
          total_pages: agentTotalPages,
          has_next: validAgentPage < agentTotalPages,
          has_prev: validAgentPage > 1
        },
        user_info: mockUserInfo
      };
    },

    userSecurityData: async (userId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: User security data requested for ID:', userId);

      return {
        user_id: userId,
        last_login: generateRandomDate('2025-01-01', '2025-01-15'),
        login_attempts: Math.floor(Math.random() * 10) + 1,
        failed_login_attempts: Math.floor(Math.random() * 3),
        password_last_changed: generateRandomDate('2024-06-01', '2024-12-31'),
        two_factor_enabled: Math.random() > 0.4,
        security_questions_set: Math.random() > 0.3,
        account_locked: Math.random() > 0.95,
        suspicious_activity_detected: Math.random() > 0.9,

        location_history: Array.from({ length: 10 }, (_, i) => ({
          id: i + 1,
          latitude: -1.9403 + (Math.random() - 0.5) * 2,
          longitude: 30.0588 + (Math.random() - 0.5) * 2,
          city: getRandomDistrict(),
          country: 'Rwanda',
          ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          device: Math.random() > 0.5 ? 'Mobile' : 'Desktop',
          browser: ['Chrome', 'Firefox', 'Safari', 'Edge'][Math.floor(Math.random() * 4)],
          timestamp: generateRandomDate('2024-12-01', '2025-01-15'),
          action: ['Login', 'Transaction', 'Profile Update', 'Logout'][Math.floor(Math.random() * 4)]
        })),

        registered_devices: Array.from({ length: Math.floor(Math.random() * 3) + 1 }, (_, i) => ({
          id: i + 1,
          device_name: ['iPhone 15', 'Samsung Galaxy S24', 'MacBook Pro', 'Windows PC'][Math.floor(Math.random() * 4)],
          device_type: Math.random() > 0.5 ? 'mobile' : 'desktop',
          last_used: generateRandomDate('2024-12-01', '2025-01-15'),
          is_trusted: Math.random() > 0.2,
          ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`
        }))
      };
    },

    userDocuments: async (userId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: User documents requested for ID:', userId);

      return {
        user_id: userId,
        documents: [
          {
            id: 1,
            type: 'national_id',
            file_name: 'national_id.pdf',
            file_url: '/mock/documents/national_id.pdf',
            status: 'approved',
            uploaded_at: generateRandomDate('2024-01-01', '2025-01-01'),
            verified_at: generateRandomDate('2024-01-01', '2025-01-01')
          },
          {
            id: 2,
            type: 'proof_of_address',
            file_name: 'utility_bill.pdf',
            file_url: '/mock/documents/utility_bill.pdf',
            status: Math.random() > 0.3 ? 'approved' : 'pending',
            uploaded_at: generateRandomDate('2024-01-01', '2025-01-01'),
            verified_at: Math.random() > 0.3 ? generateRandomDate('2024-01-01', '2025-01-01') : null
          }
        ]
      };
    },

    transactions: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Transactions requested with params:', params);
      if (isDevelopment()) console.log('🧪 Testing Mode: Raw transactions data length:', rawTransactionsData?.length || 0);
      if (isDevelopment()) console.log('🧪 Testing Mode: Enhanced transactions data length:', transactionsData?.length || 0);

      const {
        page = 1,
        limit = 10,
        status,
        search,
        start_date,
        end_date,
        type,
        min_amount,
        max_amount,
        currency,
        user_type
      } = params;

      // Generate user names for transactions
      const userNames = ['John Uwimana', 'Sarah Mukamana', 'Peter Nshimiyimana', 'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine', 'Bob Hakizimana', 'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza'];

      // Enhance transactions with user information for table display
      let filteredTransactions = transactionsData.map(tx => ({
        ...tx,
        user: {
          name: userNames[Math.floor(Math.random() * userNames.length)],
          avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 99) + 1}.jpg`,
          id: tx.sender_id || `USER-${Math.floor(Math.random() * 10000)}`
        }
      }));

      if (isDevelopment()) console.log('🧪 Testing Mode: Enhanced transactions length before filtering:', filteredTransactions?.length || 0);

      // Apply filters
      if (status) {
        filteredTransactions = filteredTransactions.filter(t => t.status === status);
      }
      if (search) {
        filteredTransactions = filteredTransactions.filter(t =>
          t.id.toLowerCase().includes(search.toLowerCase()) ||
          t.type.toLowerCase().includes(search.toLowerCase()) ||
          t.user.name.toLowerCase().includes(search.toLowerCase())
        );
      }
      if (type) {
        filteredTransactions = filteredTransactions.filter(t => t.type === type);
      }
      if (currency) {
        filteredTransactions = filteredTransactions.filter(t => t.currency === currency);
      }
      if (min_amount) {
        filteredTransactions = filteredTransactions.filter(t => t.amount >= parseFloat(min_amount));
      }
      if (max_amount) {
        filteredTransactions = filteredTransactions.filter(t => t.amount <= parseFloat(max_amount));
      }
      if (start_date) {
        filteredTransactions = filteredTransactions.filter(t => new Date(t.date) >= new Date(start_date));
      }
      if (end_date) {
        filteredTransactions = filteredTransactions.filter(t => new Date(t.date) <= new Date(end_date));
      }
      if (user_type && user_type !== 'User') {
        filteredTransactions = filteredTransactions.filter(t => t.user_type === user_type.toLowerCase());
      }

      if (isDevelopment()) console.log('🧪 Testing Mode: Filtered transactions length:', filteredTransactions?.length || 0);

      const totalItems = filteredTransactions.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);
      const paginatedTransactions = filteredTransactions.slice(start, end);

      if (isDevelopment()) console.log('🧪 Testing Mode: Paginated transactions length:', paginatedTransactions?.length || 0);

      const result = {
        success: true,
        data: {
          transactions: paginatedTransactions,
          pagination: {
            total: totalItems,
            page: validPage,
            limit: parseInt(limit),
            total_pages: totalPages,
            current_page: validPage,
            total_items: totalItems,
            items_per_page: parseInt(limit),
            has_next: validPage < totalPages,
            has_prev: validPage > 1
          }
        }
      };

      if (isDevelopment()) console.log('🧪 Testing Mode: Final transactions result structure:', {
        success: result.success,
        transactions_count: result.data?.transactions?.length || 0,
        pagination: result.data?.pagination
      });

      return result;
    },

    transactionDetails: async (transactionId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Transaction details requested for ID:', transactionId);

      // Find base transaction
      let baseTransaction = transactionsData.find(t => t.id === transactionId);
      if (!baseTransaction) {
        baseTransaction = {
          id: transactionId,
          type: ['transfer', 'deposit', 'withdrawal'][Math.floor(Math.random() * 3)],
          amount: Math.floor(Math.random() * 500000) + 10000,
          currency: ['RWF', 'GHS', 'USD'][Math.floor(Math.random() * 3)],
          status: ['completed', 'pending', 'failed'][Math.floor(Math.random() * 3)],
          date: generateRandomDate('2025-01-01', '2025-12-31')
        };
      }

      // Generate detailed transaction data
      const senderNames = ['John Uwimana', 'Sarah Mukamana', 'Peter Nshimiyimana', 'Grace Uwimana', 'David Niyonzima'];
      const receiverNames = ['Alice Mukasine', 'Bob Hakizimana', 'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza'];
      const phoneNumbers = ['+250 788 123 456', '+250 789 234 567', '+250 790 345 678', '+250 791 456 789', '+250 792 567 890'];

      const senderName = senderNames[Math.floor(Math.random() * senderNames.length)];
      const receiverName = receiverNames[Math.floor(Math.random() * receiverNames.length)];

      const sender = {
        role: 'sender',
        user: {
          id: `USER-${Math.floor(Math.random() * 10000)}`,
          name: senderName,
          phone: phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)],
          email: `${senderName.toLowerCase().replace(' ', '.')}@example.com`,
          avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 99) + 1}.jpg`,
          verified: Math.random() > 0.3,
          wallet: {
            currency: baseTransaction.currency || 'RWF',
            country_code: baseTransaction.currency === 'GHS' ? 'GH' : baseTransaction.currency === 'USD' ? 'US' : 'RW',
            balance: Math.floor(Math.random() * 500000) + 10000,
            account_number: `ACC-${Math.floor(Math.random() * 1000000)}`
          }
        }
      };

      const receiver = {
        role: 'receiver',
        user: {
          id: `USER-${Math.floor(Math.random() * 10000)}`,
          name: receiverName,
          phone: phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)],
          email: `${receiverName.toLowerCase().replace(' ', '.')}@example.com`,
          avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 99) + 1}.jpg`,
          verified: Math.random() > 0.3,
          wallet: {
            currency: baseTransaction.currency || 'RWF',
            country_code: baseTransaction.currency === 'GHS' ? 'GH' : baseTransaction.currency === 'USD' ? 'US' : 'RW',
            balance: Math.floor(Math.random() * 500000) + 10000,
            account_number: `ACC-${Math.floor(Math.random() * 1000000)}`
          }
        }
      };

      return {
        success: true,
        data: {
          transaction: {
            id: transactionId,
            type: baseTransaction.type,
            amount: baseTransaction.amount,
            currency: 'RWF',
            status: baseTransaction.status,
            date: baseTransaction.date,
            created_at: new Date(baseTransaction.date).toISOString(),
            description: `${baseTransaction.type} transaction from ${sender.user.name} to ${receiver.user.name}`,
            reference: `REF-${transactionId}-${Date.now()}`,
            fee: Math.floor(baseTransaction.amount * 0.02), // 2% fee
            total_amount: baseTransaction.amount + Math.floor(baseTransaction.amount * 0.02),

            // Transaction actors (sender and receiver)
            actors: [sender, receiver],

            // Transaction metadata
            metadata: {
              device: Math.random() > 0.5 ? 'Mobile App' : 'Web Platform',
              ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
              location: 'Kigali, Rwanda',
              user_agent: 'Mozilla/5.0 (compatible; InversePay/1.0)',
              platform: Math.random() > 0.5 ? 'iOS' : 'Android'
            },

            // Status history
            status_history: [
              {
                status: 'initiated',
                timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
                note: 'Transaction initiated by user'
              },
              {
                status: 'processing',
                timestamp: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
                note: 'Transaction being processed'
              },
              {
                status: baseTransaction.status,
                timestamp: new Date().toISOString(),
                note: `Transaction ${baseTransaction.status}`
              }
            ]
          }
        }
      };
    },

    transactionStats: async (startDate, endDate) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Transaction stats requested', { startDate, endDate });

      // Filter transactions by date range if provided
      let filteredTransactions = transactionsData;
      if (startDate || endDate) {
        filteredTransactions = transactionsData.filter(t => {
          const transactionDate = new Date(t.date);
          const start = startDate ? new Date(startDate) : null;
          const end = endDate ? new Date(endDate) : null;

          if (start && transactionDate < start) return false;
          if (end && transactionDate > end) return false;
          return true;
        });
      }

      // Calculate stats
      const totalAmount = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
      const totalTransactions = filteredTransactions.length;
      const completedTransactions = filteredTransactions.filter(t => t.status === 'completed').length;
      const pendingTransactions = filteredTransactions.filter(t => t.status === 'pending').length;
      const failedTransactions = filteredTransactions.filter(t => t.status === 'failed').length;

      return {
        success: true,
        data: {
          total_amount: totalAmount,
          total_transactions: totalTransactions,
          completed_transactions: completedTransactions,
          pending_transactions: pendingTransactions,
          failed_transactions: failedTransactions,
          average_transaction_amount: totalTransactions > 0 ? totalAmount / totalTransactions : 0,
          success_rate: totalTransactions > 0 ? (completedTransactions / totalTransactions) * 100 : 0,

          // Additional breakdowns
          transaction_types: {
            'P2P': filteredTransactions.filter(t => t.type === 'P2P').length,
            'Bill': filteredTransactions.filter(t => t.type === 'Bill').length,
            'Cash-in': filteredTransactions.filter(t => t.type === 'Cash-in').length,
            'Cash-out': filteredTransactions.filter(t => t.type === 'Cash-out').length
          },

          currency_breakdown: {
            'RWF': totalAmount * 0.7,
            'GHS': totalAmount * 0.2,
            'USD': totalAmount * 0.1
          },

          period: {
            start_date: startDate,
            end_date: endDate
          }
        }
      };
    },

    generateReceipt: async (transactionId, format = 'pdf') => {
      await simulateDelay(800);
      if (isDevelopment()) console.log('🧪 Testing Mode: Generating receipt for transaction:', transactionId, 'format:', format);

      return {
        success: true,
        data: {
          receipt_id: `RECEIPT-${transactionId}-${Date.now()}`,
          transaction_id: transactionId,
          format: format,
          download_url: `/mock/receipts/${transactionId}-receipt.${format}`,
          generated_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
          file_size: Math.floor(Math.random() * 500) + 100 // KB
        }
      };
    },

    emailReceipt: async (transactionId, recipientEmail, message = '') => {
      await simulateDelay(1000);
      if (isDevelopment()) console.log('🧪 Testing Mode: Emailing receipt for transaction:', transactionId, 'to:', recipientEmail);

      return {
        success: true,
        data: {
          email_id: `EMAIL-${transactionId}-${Date.now()}`,
          transaction_id: transactionId,
          recipient_email: recipientEmail,
          message: message,
          sent_at: new Date().toISOString(),
          status: 'sent',
          subject: `Transaction Receipt - ${transactionId}`
        }
      };
    },

    transactionEmailReceipt: async (transactionId, emailData) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Email receipt requested for transaction:', transactionId, emailData);

      return {
        success: true,
        data: {
          message_id: `EMAIL-${Date.now()}`,
          status: 'sent',
          subject: `Transaction Receipt - ${transactionId}`
        }
      };
    }
  },

  // Notifications endpoints
  notifications: {
    getAll: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Getting notifications with params:', params);

      const { page = 1, limit = 9, search = '', status = '', start_date = '', end_date = '', sort_by = 'created_at', sort_order = 'desc' } = params;

      const rwandanNames = [
        'Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana',
        'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine', 'Bob Hakizimana',
        'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza'
      ];

      const adminNames = [
        'Admin Jean Uwimana', 'Admin Sarah Mukamana', 'Admin Peter Nshimiyimana',
        'Admin Grace Uwimana', 'Admin David Niyonzima', 'Admin Alice Mukasine'
      ];

      const notificationAdmins = [
        'Admin Jean Uwimana', 'Admin Sarah Mukamana', 'Admin Peter Nshimiyimana',
        'Admin Grace Uwimana', 'Admin David Niyonzima', 'Admin Alice Mukasine'
      ];

      const notificationTypes = [
        'system', 'security', 'compliance', 'user', 'transaction',
        'maintenance', 'feature', 'warning', 'emergency', 'promotion'
      ];

      const statusOptions = ['sent', 'scheduled', 'draft', 'canceled', 'failed'];

      // Generate comprehensive mock notifications
      const mockNotifications = Array.from({ length: 45 }, (_, i) => {
        const id = i + 1;

        // Use seeded random based on ID for consistency
        const seedRandom = (seed) => {
          const x = Math.sin(seed) * 10000;
          return x - Math.floor(x);
        };

        const typeIndex = Math.floor(seedRandom(id * 1) * notificationTypes.length);
        const statusIndex = Math.floor(seedRandom(id * 2) * statusOptions.length);
        const type = notificationTypes[typeIndex];
        const status = statusOptions[statusIndex];
        const isScheduled = status === 'scheduled';
        const createdDate = new Date(Date.now() - Math.floor(seedRandom(id * 3) * 30) * 24 * 60 * 60 * 1000);

        // Create different notification content based on type
        let title, message, avatar, icon;

        switch (type) {
          case 'system':
            title = 'System Maintenance Notification';
            message = 'InversePay system will undergo scheduled maintenance tonight from 2:00 AM to 4:00 AM EAT for performance improvements.';
            icon = null; // Use React icons instead
            break;

          case 'security':
            title = 'Security Alert - Multiple Login Attempts';
            message = 'Unusual login activity detected from IP address 196.12.34.56. Account security measures have been automatically activated.';
            icon = null; // Use React icons instead
            break;

          case 'compliance':
            title = 'KYC Document Verification Required';
            message = 'High-value transaction flagged for additional KYC verification. Customer documentation review needed within 24 hours per BNR regulations.';
            icon = null; // Use React icons instead
            break;

          case 'user':
            const userNameIndex = Math.floor(seedRandom(id * 4) * rwandanNames.length);
            const userName = rwandanNames[userNameIndex];
            title = `New User Registration - ${userName}`;
            message = `New customer ${userName} has completed registration from Kigali, Rwanda. Account awaiting admin approval for transaction limits.`;
            avatar = `/assets/images/admin_placeholder_img.png`;
            break;

          case 'transaction':
            title = 'Large Transaction Alert';
            message = 'Transaction of RWF 2,500,000 processed between Kigali and Musanze. Automatic compliance checks completed successfully.';
            icon = null; // Use React icons instead
            break;

          case 'maintenance':
            title = 'Mobile Money Integration Maintenance';
            message = 'MTN Mobile Money services will be temporarily unavailable from 1:00 AM to 3:00 AM EAT for system upgrades.';
            icon = null; // Use React icons instead
            break;

          case 'feature':
            title = 'New Feature: Multi-Currency Wallets';
            message = 'InversePay now supports GHS (Ghanaian Cedi) wallets alongside RWF. Users can now hold and exchange between currencies seamlessly.';
            icon = null; // Use React icons instead
            break;

          case 'warning':
            title = 'High Transaction Volume Warning';
            message = 'Daily transaction volume has exceeded 85% of system capacity. Consider implementing rate limiting during peak hours.';
            icon = null; // Use React icons instead
            break;

          case 'emergency':
            title = 'Emergency: Payment Gateway Connectivity Issue';
            message = 'Critical connectivity issues with Bank of Kigali payment gateway. Transactions may experience delays. Technical team investigating.';
            icon = null; // Use React icons instead
            break;

          case 'promotion':
            title = 'Special Promotion: Zero Fees Weekend';
            message = 'Announcing zero transaction fees for all P2P transfers this weekend to celebrate Rwanda Liberation Day. Promote to users via app.';
            icon = null; // Use React icons instead
            break;

          default:
            title = 'General System Notification';
            message = 'General system notification for testing purposes.';
            icon = null; // Use React icons instead
        }

        const priorityOptions = ['low', 'medium', 'high', 'urgent'];
        const targetAudienceOptions = ['all_users', 'premium_users', 'new_users', 'high_value_users'];
        const regionOptions = ['Kigali', 'Northern Province', 'Southern Province', 'Eastern Province', 'Western Province'];

        return {
          id,
          title,
          message,
          body: message, // API sometimes uses 'body' instead of 'message'
          type,
          status,
          priority: priorityOptions[Math.floor(seedRandom(id * 5) * priorityOptions.length)],

          // Display fields for the notifications page
          header: title,
          description: message,
          avatar: avatar || null,
          icon: icon || null,
          time: `${Math.floor(seedRandom(id * 6) * 59) + 1} mins ago`,
          unread: seedRandom(id * 7) > 0.3, // 70% chance of being unread

          // API fields
          created_at: createdDate.toISOString(),
          updated_at: createdDate.toISOString(),
          sent_at: status === 'sent' ? createdDate.toISOString() : null,
          scheduled_time: isScheduled ?
            new Date(Date.now() + Math.floor(seedRandom(id * 8) * 7) * 24 * 60 * 60 * 1000).toISOString() :
            null,

          // Metadata
          read_count: status === 'sent' ? Math.floor(seedRandom(id * 9) * 15000) + 1000 : 0,
          delivery_rate: status === 'sent' ? (seedRandom(id * 10) * 0.1 + 0.9) : 0, // 90-100%
          click_rate: status === 'sent' ? (seedRandom(id * 11) * 0.3 + 0.1) : 0, // 10-40%

          // Additional context
          target_audience: targetAudienceOptions[Math.floor(seedRandom(id * 12) * targetAudienceOptions.length)],
          region: regionOptions[Math.floor(seedRandom(id * 13) * regionOptions.length)],

          // Cancellation info for canceled notifications
          ...(status === 'canceled' && {
            canceled_at: new Date(createdDate.getTime() + Math.floor(seedRandom(id * 14) * 24) * 60 * 60 * 1000).toISOString(),
            cancellation_reason: 'Admin decision due to changing circumstances'
          }),

          // Review status
          reviewed: seedRandom(id * 15) > 0.2, // 80% reviewed
          reviewer_id: seedRandom(id * 16) > 0.2 ? 'admin_' + Math.floor(seedRandom(id * 17) * 5) + 1 : null,
          reviewed_at: seedRandom(id * 18) > 0.2 ?
            new Date(createdDate.getTime() + Math.floor(seedRandom(id * 19) * 2) * 60 * 60 * 1000).toISOString() :
            null,

          // Author information
          created_by: {
            id: `admin_${Math.floor(seedRandom(id * 20) * 6) + 1}`,
            name: notificationAdmins[Math.floor(seedRandom(id * 21) * notificationAdmins.length)],
            email: `admin${Math.floor(seedRandom(id * 22) * 6) + 1}@inversepal.com`,
            avatar: '/assets/images/admin_placeholder_img.png',
            role: 'Admin'
          },

          // Analytics data for detail page
          analytics: status === 'sent' ? {
            delivered: Math.floor(seedRandom(id * 23) * 15000) + 5000,
            clicked: Math.floor(seedRandom(id * 24) * 3000) + 500,
            performance: seedRandom(id * 25) > 0.7 ? 'high' : seedRandom(id * 26) > 0.4 ? 'medium' : 'low',
            click_rate: seedRandom(id * 27) * 0.3 + 0.1
          } : null
        };
      });

      // Apply filters
      let filteredNotifications = mockNotifications;

      // Search filter
      if (search) {
        filteredNotifications = filteredNotifications.filter(notif =>
          notif.title.toLowerCase().includes(search.toLowerCase()) ||
          notif.message.toLowerCase().includes(search.toLowerCase()) ||
          notif.type.toLowerCase().includes(search.toLowerCase())
        );
      }

      // Status filter
      if (status) {
        filteredNotifications = filteredNotifications.filter(notif => notif.status === status);
      }

      // Date range filter
      if (start_date) {
        filteredNotifications = filteredNotifications.filter(notif =>
          new Date(notif.created_at) >= new Date(start_date)
        );
      }
      if (end_date) {
        filteredNotifications = filteredNotifications.filter(notif =>
          new Date(notif.created_at) <= new Date(end_date)
        );
      }

      // Sorting
      filteredNotifications.sort((a, b) => {
        const aVal = a[sort_by];
        const bVal = b[sort_by];

        if (sort_order === 'desc') {
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        } else {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
      });

      // Pagination
      const totalItems = filteredNotifications.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);
      const paginatedNotifications = filteredNotifications.slice(start, end);

      return {
        notifications: paginatedNotifications,
        pagination: {
          current_page: validPage,
          total_pages: totalPages,
          total_items: totalItems,
          items_per_page: parseInt(limit),
          has_next: validPage < totalPages,
          has_prev: validPage > 1,
        },
        success: true
      };
    },

    create: async (notificationData) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Creating notification', notificationData);

      const newNotification = {
        id: Date.now(),
        ...notificationData,
        // Ensure both body and message fields are present
        body: notificationData.body || notificationData.message,
        message: notificationData.message || notificationData.body,
        status: notificationData.scheduled_time ? 'scheduled' : 'sent',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sent_at: !notificationData.scheduled_time ? new Date().toISOString() : null,
        read_count: 0,
        delivery_rate: 0,
        click_rate: 0,
        reviewed: false,
        reviewer_id: null,
        reviewed_at: null,

        // Display fields
        header: notificationData.title,
        description: notificationData.message || notificationData.body,
        time: 'Just now',
        unread: true,
        avatar: null,
        icon: null // Use React icons instead
      };

      return newNotification;
    },

    getById: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Getting notification by ID', id);

      // Use the same data generation logic as getAll to find the specific notification
      const rwandanNames = [
        'Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana',
        'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine', 'Bob Hakizimana',
        'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza'
      ];

      const notificationAdmins = [
        'Admin Jean Uwimana', 'Admin Sarah Mukamana', 'Admin Peter Nshimiyimana',
        'Admin Grace Uwimana', 'Admin David Niyonzima', 'Admin Alice Mukasine'
      ];

      const notificationTypes = [
        'system', 'security', 'compliance', 'user', 'transaction',
        'maintenance', 'feature', 'warning', 'emergency', 'promotion'
      ];

      const statusOptions = ['sent', 'scheduled', 'draft', 'canceled', 'failed'];

      // Generate all notifications to find the one with matching ID
      const allNotifications = Array.from({ length: 45 }, (_, i) => {
        const notifId = i + 1;

        // Use seeded random based on ID for consistency
        const seedRandom = (seed) => {
          const x = Math.sin(seed) * 10000;
          return x - Math.floor(x);
        };

        const typeIndex = Math.floor(seedRandom(notifId * 1) * notificationTypes.length);
        const statusIndex = Math.floor(seedRandom(notifId * 2) * statusOptions.length);
        const type = notificationTypes[typeIndex];
        const status = statusOptions[statusIndex];
        const isScheduled = status === 'scheduled';
        const createdDate = new Date(Date.now() - Math.floor(seedRandom(notifId * 3) * 30) * 24 * 60 * 60 * 1000);

        // Create different notification content based on type
        let title, message, avatar, icon;

        switch (type) {
          case 'system':
            title = 'System Maintenance Notification';
            message = 'InversePay system will undergo scheduled maintenance tonight from 2:00 AM to 4:00 AM EAT for performance improvements.';
            icon = null; // Use React icons instead
            break;

          case 'security':
            title = 'Security Alert - Multiple Login Attempts';
            message = 'Unusual login activity detected from IP address 196.12.34.56. Account security measures have been automatically activated.';
            icon = null; // Use React icons instead
            break;

          case 'compliance':
            title = 'KYC Document Verification Required';
            message = 'High-value transaction flagged for additional KYC verification. Customer documentation review needed within 24 hours per BNR regulations.';
            icon = null; // Use React icons instead
            break;

          case 'user':
            const userNameIndex = Math.floor(seedRandom(notifId * 4) * rwandanNames.length);
            const userName = rwandanNames[userNameIndex];
            title = `New User Registration - ${userName}`;
            message = `New customer ${userName} has completed registration from Kigali, Rwanda. Account awaiting admin approval for transaction limits.`;
            avatar = `/assets/images/admin_placeholder_img.png`;
            break;

          case 'transaction':
            title = 'Large Transaction Alert';
            message = 'Transaction of RWF 2,500,000 processed between Kigali and Musanze. Automatic compliance checks completed successfully.';
            icon = null; // Use React icons instead
            break;

          case 'maintenance':
            title = 'Mobile Money Integration Maintenance';
            message = 'MTN Mobile Money services will be temporarily unavailable from 1:00 AM to 3:00 AM EAT for system upgrades.';
            icon = null; // Use React icons instead
            break;

          case 'feature':
            title = 'New Feature: Multi-Currency Wallets';
            message = 'InversePay now supports GHS (Ghanaian Cedi) wallets alongside RWF. Users can now hold and exchange between currencies seamlessly.';
            icon = null; // Use React icons instead
            break;

          case 'warning':
            title = 'High Transaction Volume Warning';
            message = 'Daily transaction volume has exceeded 85% of system capacity. Consider implementing rate limiting during peak hours.';
            icon = null; // Use React icons instead
            break;

          case 'emergency':
            title = 'Emergency: Payment Gateway Connectivity Issue';
            message = 'Critical connectivity issues with Bank of Kigali payment gateway. Transactions may experience delays. Technical team investigating.';
            icon = null; // Use React icons instead
            break;

          case 'promotion':
            title = 'Special Promotion: Zero Fees Weekend';
            message = 'Announcing zero transaction fees for all P2P transfers this weekend to celebrate Rwanda Liberation Day. Promote to users via app.';
            icon = null; // Use React icons instead
            break;

          default:
            title = 'General System Notification';
            message = 'General system notification for testing purposes.';
            icon = null; // Use React icons instead
        }

        const priorityOptions = ['low', 'medium', 'high', 'urgent'];
        const targetAudienceOptions = ['all_users', 'premium_users', 'new_users', 'high_value_users'];
        const regionOptions = ['Kigali', 'Northern Province', 'Southern Province', 'Eastern Province', 'Western Province'];

        return {
          id: notifId,
          title,
          message,
          body: message,
          type,
          status,
          priority: priorityOptions[Math.floor(seedRandom(notifId * 5) * priorityOptions.length)],

          // Display fields for the notifications page
          header: title,
          description: message,
          avatar: avatar || null,
          icon: icon || null,
          time: `${Math.floor(seedRandom(notifId * 6) * 59) + 1} mins ago`,
          unread: seedRandom(notifId * 7) > 0.3, // 70% chance of being unread

          // API fields
          created_at: createdDate.toISOString(),
          updated_at: createdDate.toISOString(),
          sent_at: status === 'sent' ? createdDate.toISOString() : null,
          scheduled_time: isScheduled ?
            new Date(Date.now() + Math.floor(seedRandom(notifId * 8) * 7) * 24 * 60 * 60 * 1000).toISOString() :
            null,

          // Metadata
          read_count: status === 'sent' ? Math.floor(seedRandom(notifId * 9) * 15000) + 1000 : 0,
          delivery_rate: status === 'sent' ? (seedRandom(notifId * 10) * 0.1 + 0.9) : 0, // 90-100%
          click_rate: status === 'sent' ? (seedRandom(notifId * 11) * 0.3 + 0.1) : 0, // 10-40%

          // Additional context
          target_audience: targetAudienceOptions[Math.floor(seedRandom(notifId * 12) * targetAudienceOptions.length)],
          region: regionOptions[Math.floor(seedRandom(notifId * 13) * regionOptions.length)],

          // Cancellation info for canceled notifications
          ...(status === 'canceled' && {
            canceled_at: new Date(createdDate.getTime() + Math.floor(seedRandom(notifId * 14) * 24) * 60 * 60 * 1000).toISOString(),
            cancellation_reason: 'Admin decision due to changing circumstances'
          }),

          // Review status
          reviewed: seedRandom(notifId * 15) > 0.2, // 80% reviewed
          reviewer_id: seedRandom(notifId * 16) > 0.2 ? 'admin_' + Math.floor(seedRandom(notifId * 17) * 5) + 1 : null,
          reviewed_at: seedRandom(notifId * 18) > 0.2 ?
            new Date(createdDate.getTime() + Math.floor(seedRandom(notifId * 19) * 2) * 60 * 60 * 1000).toISOString() :
            null,

          // Author information
          created_by: {
            id: `admin_${Math.floor(seedRandom(notifId * 20) * 6) + 1}`,
            name: notificationAdmins[Math.floor(seedRandom(notifId * 21) * notificationAdmins.length)],
            email: `admin${Math.floor(seedRandom(notifId * 22) * 6) + 1}@inversepal.com`,
            avatar: '/assets/images/admin_placeholder_img.png',
            role: 'Admin'
          },

          // Analytics data for detail page
          analytics: status === 'sent' ? {
            delivered_count: Math.floor(seedRandom(notifId * 23) * 15000) + 5000,
            click_count: Math.floor(seedRandom(notifId * 24) * 3000) + 500,
            performance: seedRandom(notifId * 25) > 0.7 ? 'high' : seedRandom(notifId * 26) > 0.4 ? 'medium' : 'low',
            performance_summary: status === 'sent' ? 
              (seedRandom(notifId * 25) > 0.7 ? 'Excellent engagement with high click-through rates.' : 
               seedRandom(notifId * 26) > 0.4 ? 'Good performance with moderate user engagement.' : 
               'Low engagement rate. Consider reviewing notification content and timing.') : 
              'Analytics aren\'t available yet since this notification hasn\'t been delivered. Check back after it goes live.',
            click_rate: seedRandom(notifId * 27) * 0.3 + 0.1,
            delivery_rate: seedRandom(notifId * 28) * 0.1 + 0.9, // 90-100%
            open_rate: seedRandom(notifId * 29) * 0.4 + 0.2 // 20-60%
          } : null
        };
      });

      // Find the notification with the matching ID
      const targetId = parseInt(id);
      const notification = allNotifications.find(n => n.id === targetId);

      if (!notification) {
        throw new Error(`Notification with ID ${id} not found`);
      }

      return notification;
    },

    update: async (id, updateData) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Updating notification', id, updateData);

      return {
        id: parseInt(id),
        ...updateData,
        updated_at: new Date().toISOString(),
        success: true
      };
    },

    delete: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Deleting notification', id);

      return {
        success: true,
        message: 'Notification deleted successfully'
      };
    },

    schedule: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Scheduling notification', id);

      return {
        id: parseInt(id),
        status: 'scheduled',
        scheduled_time: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date().toISOString(),
        success: true
      };
    },

    send: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Sending notification immediately', id);

      return {
        id: parseInt(id),
        status: 'sent',
        sent_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        success: true
      };
    },

    cancel: async (id, reason) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Canceling notification', id, 'Reason:', reason);

      return {
        id: parseInt(id),
        status: 'canceled',
        canceled_at: new Date().toISOString(),
        cancellation_reason: reason,
        updated_at: new Date().toISOString(),
        success: true
      };
    },

    resend: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Resending notification', id);

      const newId = Date.now();
      return {
        id: newId,
        original_id: parseInt(id),
        status: 'sent',
        sent_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        title: 'Resent Notification',
        message: 'This notification has been resent as a new instance.',
        body: 'This notification has been resent as a new instance.',
        type: 'general',
        success: true
      };
    },

    getAnalytics: async (id) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Getting notification analytics', id);

      const deliveredCount = 14891;
      const clickCount = 721;
      const clickRate = 0.047;

      return {
        id: parseInt(id),
        total_recipients: 15420,
        delivered_count: deliveredCount,
        delivery_rate: 0.967,
        opened: 3608,
        open_rate: 0.234,
        click_count: clickCount,
        click_rate: clickRate,
        bounced: 529,
        bounce_rate: 0.034,
        performance_summary: clickRate >= 0.05 ? 'Excellent engagement with high click-through rates.' : 
                           clickRate >= 0.025 ? 'Good performance with moderate user engagement.' : 
                           'Low engagement rate. Consider reviewing notification content and timing.',

        // Time-based analytics
        hourly_metrics: Array.from({ length: 24 }, (_, i) => ({
          hour: i,
          delivered: Math.floor(Math.random() * 1000) + 100,
          opened: Math.floor(Math.random() * 200) + 20,
          clicked: Math.floor(Math.random() * 50) + 5
        })),

        success: true
      };
    },

    getAnalyticsSummary: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Getting analytics summary');

      return {
        total_notifications_sent: 1247,
        total_recipients_reached: 187634,
        average_delivery_rate: 0.943,
        average_open_rate: 0.287,
        average_click_rate: 0.052,

        // Monthly trends
        monthly_trends: Array.from({ length: 12 }, (_, i) => ({
          month: new Date(2024, i).toLocaleDateString('en-US', { month: 'short' }),
          notifications_sent: Math.floor(Math.random() * 150) + 50,
          total_recipients: Math.floor(Math.random() * 20000) + 10000,
          delivery_rate: Math.random() * 0.1 + 0.9,
          open_rate: Math.random() * 0.2 + 0.2,
          click_rate: Math.random() * 0.08 + 0.02
        })),

        // Performance by type
        performance_by_type: [
          { type: 'system', sent: 234, delivery_rate: 0.98, open_rate: 0.45, click_rate: 0.08 },
          { type: 'security', sent: 189, delivery_rate: 0.96, open_rate: 0.67, click_rate: 0.12 },
          { type: 'compliance', sent: 156, delivery_rate: 0.94, open_rate: 0.78, click_rate: 0.15 },
          { type: 'user', sent: 298, delivery_rate: 0.92, open_rate: 0.34, click_rate: 0.06 },
          { type: 'transaction', sent: 178, delivery_rate: 0.97, open_rate: 0.56, click_rate: 0.09 },
          { type: 'promotion', sent: 192, delivery_rate: 0.89, open_rate: 0.42, click_rate: 0.18 }
        ],

        success: true
      };
    }
  },

  // Analytics & Reporting endpoints
  analytics: {
    transactionVolume: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Transaction volume analytics requested', params);

      const monthlyData = [
        { name: 'Jan', transactions: 420, volume: 125000 },
        { name: 'Feb', transactions: 380, volume: 115000 },
        { name: 'Mar', transactions: 520, volume: 165000 },
        { name: 'Apr', transactions: 480, volume: 145000 },
        { name: 'May', transactions: 620, volume: 185000 },
        { name: 'Jun', transactions: 580, volume: 175000 },
        { name: 'Jul', transactions: 720, volume: 225000 },
        { name: 'Aug', transactions: 680, volume: 205000 },
        { name: 'Sep', transactions: 750, volume: 235000 },
        { name: 'Oct', transactions: 820, volume: 265000 },
        { name: 'Nov', transactions: 880, volume: 285000 },
        { name: 'Dec', transactions: 920, volume: 305000 }
      ];

      return {
        success: true,
        data: monthlyData
      };
    },

    transactionDistribution: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Transaction distribution analytics requested', params);

      return {
        success: true,
        data: [
          { name: 'P2P Transfer', value: 45, count: 2340, percentage: 45, color: '#9b51e0' },
          { name: 'Bill Payment', value: 25, count: 1300, percentage: 25, color: '#FC4D50' },
          { name: 'Cash-in', value: 18, count: 936, percentage: 18, color: '#F59E0B' },
          { name: 'Cash-out', value: 12, count: 624, percentage: 12, color: '#A855F7' }
        ]
      };
    },

    revenueBreakdown: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Revenue breakdown analytics requested', params);

      return {
        success: true,
        data: [
          { name: 'Transaction Fees', value: 65, amount: 45500, color: '#9b51e0' },
          { name: 'Exchange Fees', value: 20, amount: 14000, color: '#FC4D50' },
          { name: 'Premium Services', value: 10, amount: 7000, color: '#F59E0B' },
          { name: 'Agent Commission', value: 5, amount: 3500, color: '#A855F7' }
        ]
      };
    },

    revenueTrend: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Revenue trend analytics requested', params);

      const trendData = [
        { month: 'Jan', revenue: 45000, growth: 5.2 },
        { month: 'Feb', revenue: 42000, growth: -6.7 },
        { month: 'Mar', revenue: 58000, growth: 38.1 },
        { month: 'Apr', revenue: 55000, growth: -5.2 },
        { month: 'May', revenue: 67000, growth: 21.8 },
        { month: 'Jun', revenue: 72000, growth: 7.5 },
        { month: 'Jul', revenue: 85000, growth: 18.1 },
        { month: 'Aug', revenue: 78000, growth: -8.2 },
        { month: 'Sep', revenue: 92000, growth: 17.9 },
        { month: 'Oct', revenue: 98000, growth: 6.5 },
        { month: 'Nov', revenue: 105000, growth: 7.1 },
        { month: 'Dec', revenue: 112000, growth: 6.7 }
      ];

      return {
        success: true,
        data: trendData
      };
    },

    userEngagement: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: User engagement analytics requested', params);

      const engagementData = [
        {
          month: 'Jan',
          period: 'Jan',
          activeUsers: 12500,
          newUsers: 1200,
          firstTime: 1200,
          returningUsers: 11300,
          returning: 11300
        },
        {
          month: 'Feb',
          period: 'Feb',
          activeUsers: 13200,
          newUsers: 1400,
          firstTime: 1400,
          returningUsers: 11800,
          returning: 11800
        },
        {
          month: 'Mar',
          period: 'Mar',
          activeUsers: 14800,
          newUsers: 1800,
          firstTime: 1800,
          returningUsers: 13000,
          returning: 13000
        },
        {
          month: 'Apr',
          period: 'Apr',
          activeUsers: 15600,
          newUsers: 1600,
          firstTime: 1600,
          returningUsers: 14000,
          returning: 14000
        },
        {
          month: 'May',
          period: 'May',
          activeUsers: 17200,
          newUsers: 2000,
          firstTime: 2000,
          returningUsers: 15200,
          returning: 15200
        },
        {
          month: 'Jun',
          period: 'Jun',
          activeUsers: 18500,
          newUsers: 2200,
          firstTime: 2200,
          returningUsers: 16300,
          returning: 16300
        }
      ];

      return {
        success: true,
        data: engagementData
      };
    },

    retentionRate: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Retention rate analytics requested', params);

      const retentionPercentage = 75; // Overall retention rate
      const totalUsers = 15000;
      const retainedUsers = Math.floor(totalUsers * (retentionPercentage / 100));

      // Pie chart data - retained vs churned
      const retentionData = [
        {
          name: 'Retained Users',
          value: retentionPercentage,
          count: retainedUsers,
          color: '#9b51e0'
        },
        {
          name: 'Churned Users',
          value: 100 - retentionPercentage,
          count: totalUsers - retainedUsers,
          color: '#E5E7EB'
        }
      ];

      return {
        success: true,
        data: retentionData,
        retention_rate: retentionPercentage,
        total_users_analyzed: totalUsers,
        retained_users_count: retainedUsers,
        churned_users_count: totalUsers - retainedUsers
      };
    },

    topUsers: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Top users analytics requested', params);

      const topUsersNames = [
        'John Uwimana', 'Sarah Mukamana', 'Peter Nshimiyimana', 'Grace Uwimana', 'David Niyonzima',
        'Alice Mukasine', 'Bob Hakizimana', 'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza',
        'James Nkurunziza', 'Mary Uwimana', 'Paul Nsengimana', 'Joyce Mukanyandwi', 'Simon Habimana',
        'Rose Uwamahoro', 'Eric Niyonzima', 'Linda Mukamana', 'Victor Uwimana', 'Stella Mukamusoni'
      ];

      const transactionTypes = ['P2P Transfer', 'Bill Payment', 'Cash-in', 'Cash-out'];

      const topUsers = topUsersNames.map((name, index) => ({
        user_id: `USER-${1000 + index}`,
        id: `USER-${1000 + index}`,
        name: name,
        full_name: name,
        avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 99) + 1}.jpg`,

        // Volume and value data
        transaction_volume: (150 - (index * 5)).toString(),
        total_transactions: (150 - (index * 5)).toString(),
        transaction_value: `RWF ${(250000 - (index * 10000)).toLocaleString()}`,
        total_value: `RWF ${(250000 - (index * 10000)).toLocaleString()}`,

        // Transaction preferences
        preferred_transaction_type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
        preferred_type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
        least_used_transaction_type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
        least_type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],

        // Activity data
        last_activity: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toLocaleDateString(),
        last_activity_date: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toLocaleDateString(),

        rank: index + 1,
        growth: Math.floor(Math.random() * 30) + 5
      }));

      return {
        success: true,
        data: topUsers
      };
    },

    cashFlow: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Cash flow analytics requested', params);

      const cashFlowData = [
        {
          month: 'Jan',
          period: 'Jan',
          cashIn: 125000,
          topUps: 125000,
          cashOut: 98000,
          withdrawals: 98000,
          net: 27000
        },
        {
          month: 'Feb',
          period: 'Feb',
          cashIn: 142000,
          topUps: 142000,
          cashOut: 105000,
          withdrawals: 105000,
          net: 37000
        },
        {
          month: 'Mar',
          period: 'Mar',
          cashIn: 158000,
          topUps: 158000,
          cashOut: 118000,
          withdrawals: 118000,
          net: 40000
        },
        {
          month: 'Apr',
          period: 'Apr',
          cashIn: 165000,
          topUps: 165000,
          cashOut: 125000,
          withdrawals: 125000,
          net: 40000
        },
        {
          month: 'May',
          period: 'May',
          cashIn: 182000,
          topUps: 182000,
          cashOut: 138000,
          withdrawals: 138000,
          net: 44000
        },
        {
          month: 'Jun',
          period: 'Jun',
          cashIn: 195000,
          topUps: 195000,
          cashOut: 145000,
          withdrawals: 145000,
          net: 50000
        }
      ];

      return {
        success: true,
        data: cashFlowData
      };
    },

    cashOutComparison: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Cash out comparison analytics requested', params);

      const comparisonData = [
        { method: 'Bank Transfer', name: 'Bank Transfer', value: 45, percentage: 45, amount: 89000, trend: 'up', color: '#9b51e0' },
        { method: 'Agent', name: 'Agent', value: 35, percentage: 35, amount: 69300, trend: 'up', color: '#FC4D50' },
        { method: 'ATM', name: 'ATM', value: 15, percentage: 15, amount: 29700, trend: 'down', color: '#F59E0B' },
        { method: 'Mobile Money', name: 'Mobile Money', value: 5, percentage: 5, amount: 9900, trend: 'stable', color: '#A855F7' }
      ];

      return {
        success: true,
        data: comparisonData
      };
    },

    netCashFlow: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Net cash flow analytics requested', params);

      const netFlowData = Array.from({ length: 30 }, (_, i) => {
        const netFlow = Math.floor(Math.random() * 20000) - 5000;
        return {
          day: i + 1,
          period: `Day ${i + 1}`,
          date: new Date(2024, 11, i + 1).toLocaleDateString(),
          netFlow: netFlow,
          net_cash_flow: netFlow,
          inflow: Math.floor(Math.random() * 15000) + 5000,
          outflow: Math.floor(Math.random() * 12000) + 3000
        };
      });

      return {
        success: true,
        data: netFlowData
      };
    },

    billDistribution: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Bill distribution analytics requested', params);

      return {
        success: true,
        data: [
          { name: 'Electricity', value: 35, count: 890, color: '#9b51e0' },
          { name: 'Water', value: 25, count: 650, color: '#FC4D50' },
          { name: 'Mobile', value: 20, count: 1200, color: '#F59E0B' },
          { name: 'Internet', value: 15, count: 420, color: '#A855F7' },
          { name: 'TV', value: 5, count: 380, color: '#10B981' }
        ]
      };
    },

    billPayments: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Bill payments analytics requested', params);

      // Time series data for line chart
      const billTimeSeriesData = [
        {
          month: 'Jan',
          period: 'Jan',
          wifi: 420,
          electricity: 890,
          water: 650,
          mobile: 1200,
          tv: 380
        },
        {
          month: 'Feb',
          period: 'Feb',
          wifi: 445,
          electricity: 920,
          water: 680,
          mobile: 1250,
          tv: 400
        },
        {
          month: 'Mar',
          period: 'Mar',
          wifi: 470,
          electricity: 950,
          water: 710,
          mobile: 1300,
          tv: 420
        },
        {
          month: 'Apr',
          period: 'Apr',
          wifi: 460,
          electricity: 930,
          water: 690,
          mobile: 1280,
          tv: 410
        },
        {
          month: 'May',
          period: 'May',
          wifi: 490,
          electricity: 980,
          water: 740,
          mobile: 1350,
          tv: 450
        },
        {
          month: 'Jun',
          period: 'Jun',
          wifi: 510,
          electricity: 1010,
          water: 770,
          mobile: 1400,
          tv: 480
        }
      ];

      return {
        success: true,
        data: billTimeSeriesData
      };
    }
  },

  // Exchange rates endpoints
  exchangeRates: {
    rates: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Exchange rates requested', params);

      const { page = 1, limit = 10, search, from, to } = params;

      // Currency mappings for country codes and names
      const currencyInfo = {
        'USD': { country: 'US', name: 'US Dollar' },
        'EUR': { country: 'EU', name: 'Euro' },
        'GBP': { country: 'GB', name: 'British Pound' },
        'RWF': { country: 'RW', name: 'Rwandan Franc' },
        'GHS': { country: 'GH', name: 'Ghanaian Cedi' },
        'KES': { country: 'KE', name: 'Kenyan Shilling' }
      };

      const allRates = [
        {
          id: 1,
          from: 'USD',
          to: 'RWF',
          rate: 1345.50,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '+0.5%',
          trend: 'up',
          // Table fields
          baseCurrency: 'USD',
          quoteCurrency: 'RWF',
          baseCountry: 'US',
          quoteCountry: 'RW',
          baseName: 'US Dollar',
          quoteName: 'Rwandan Franc',
          pair: 'USD/RWF',
          buy: '1343.25',
          sell: '1347.75',
          spread: '4.50'
        },
        {
          id: 2,
          from: 'EUR',
          to: 'RWF',
          rate: 1425.75,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '-0.2%',
          trend: 'down',
          // Table fields
          baseCurrency: 'EUR',
          quoteCurrency: 'RWF',
          baseCountry: 'EU',
          quoteCountry: 'RW',
          baseName: 'Euro',
          quoteName: 'Rwandan Franc',
          pair: 'EUR/RWF',
          buy: '1423.50',
          sell: '1428.00',
          spread: '4.50'
        },
        {
          id: 3,
          from: 'GBP',
          to: 'RWF',
          rate: 1665.20,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '+1.1%',
          trend: 'up',
          // Table fields
          baseCurrency: 'GBP',
          quoteCurrency: 'RWF',
          baseCountry: 'GB',
          quoteCountry: 'RW',
          baseName: 'British Pound',
          quoteName: 'Rwandan Franc',
          pair: 'GBP/RWF',
          buy: '1662.70',
          sell: '1667.70',
          spread: '5.00'
        },
        {
          id: 4,
          from: 'GHS',
          to: 'RWF',
          rate: 89.45,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '+0.3%',
          trend: 'up',
          // Table fields
          baseCurrency: 'GHS',
          quoteCurrency: 'RWF',
          baseCountry: 'GH',
          quoteCountry: 'RW',
          baseName: 'Ghanaian Cedi',
          quoteName: 'Rwandan Franc',
          pair: 'GHS/RWF',
          buy: '89.20',
          sell: '89.70',
          spread: '0.50'
        },
        {
          id: 5,
          from: 'KES',
          to: 'RWF',
          rate: 10.42,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '-0.8%',
          trend: 'down',
          // Table fields
          baseCurrency: 'KES',
          quoteCurrency: 'RWF',
          baseCountry: 'KE',
          quoteCountry: 'RW',
          baseName: 'Kenyan Shilling',
          quoteName: 'Rwandan Franc',
          pair: 'KES/RWF',
          buy: '10.35',
          sell: '10.49',
          spread: '0.14'
        },
        {
          id: 6,
          from: 'USD',
          to: 'GHS',
          rate: 15.05,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '+0.2%',
          trend: 'up',
          // Table fields
          baseCurrency: 'USD',
          quoteCurrency: 'GHS',
          baseCountry: 'US',
          quoteCountry: 'GH',
          baseName: 'US Dollar',
          quoteName: 'Ghanaian Cedi',
          pair: 'USD/GHS',
          buy: '15.02',
          sell: '15.08',
          spread: '0.06'
        },
        {
          id: 7,
          from: 'EUR',
          to: 'GHS',
          rate: 15.94,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '-0.7%',
          trend: 'down',
          // Table fields
          baseCurrency: 'EUR',
          quoteCurrency: 'GHS',
          baseCountry: 'EU',
          quoteCountry: 'GH',
          baseName: 'Euro',
          quoteName: 'Ghanaian Cedi',
          pair: 'EUR/GHS',
          buy: '15.90',
          sell: '15.98',
          spread: '0.08'
        },
        {
          id: 8,
          from: 'GBP',
          to: 'GHS',
          rate: 18.62,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '+0.9%',
          trend: 'up',
          // Table fields
          baseCurrency: 'GBP',
          quoteCurrency: 'GHS',
          baseCountry: 'GB',
          quoteCountry: 'GH',
          baseName: 'British Pound',
          quoteName: 'Ghanaian Cedi',
          pair: 'GBP/GHS',
          buy: '18.58',
          sell: '18.66',
          spread: '0.08'
        },
        {
          id: 9,
          from: 'RWF',
          to: 'USD',
          rate: 0.000743,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '-0.5%',
          trend: 'down',
          // Table fields
          baseCurrency: 'RWF',
          quoteCurrency: 'USD',
          baseCountry: 'RW',
          quoteCountry: 'US',
          baseName: 'Rwandan Franc',
          quoteName: 'US Dollar',
          pair: 'RWF/USD',
          buy: '0.000741',
          sell: '0.000745',
          spread: '0.000004'
        },
        {
          id: 10,
          from: 'GHS',
          to: 'USD',
          rate: 0.0664,
          lastUpdate: '2024-12-13T10:30:00Z',
          change: '-0.2%',
          trend: 'down',
          // Table fields
          baseCurrency: 'GHS',
          quoteCurrency: 'USD',
          baseCountry: 'GH',
          quoteCountry: 'US',
          baseName: 'Ghanaian Cedi',
          quoteName: 'US Dollar',
          pair: 'GHS/USD',
          buy: '0.0662',
          sell: '0.0666',
          spread: '0.0004'
        }
      ];

      let filteredRates = allRates;

      // Apply search filter
      if (search) {
        filteredRates = filteredRates.filter(rate =>
          rate.from.toLowerCase().includes(search.toLowerCase()) ||
          rate.to.toLowerCase().includes(search.toLowerCase()) ||
          rate.pair.toLowerCase().includes(search.toLowerCase())
        );
      }

      // Apply currency filters
      if (from) {
        filteredRates = filteredRates.filter(rate => rate.from === from);
      }
      if (to) {
        filteredRates = filteredRates.filter(rate => rate.to === to);
      }

      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedRates = filteredRates.slice(start, end);

      return {
        success: true,
        rates: paginatedRates,
        pagination: {
          currentPage: parseInt(page),
          totalPages: Math.ceil(filteredRates.length / limit),
          totalItems: filteredRates.length,
          itemsPerPage: parseInt(limit)
        }
      };
    },

    historical: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Historical exchange rates requested', params);

      const { from, to, period = '1Y' } = params;

      // Generate historical data based on period
      const dataPoints = {
        '12H': 24, '1D': 24, '1W': 7, '1M': 30,
        '1Y': 12, '2Y': 24, '5Y': 60, '10Y': 120
      };

      const points = dataPoints[period] || 12;
      const baseRate = from === 'USD' && to === 'RWF' ? 1345.50 :
        from === 'EUR' && to === 'RWF' ? 1425.75 :
          from === 'GHS' && to === 'RWF' ? 89.45 : 100;

      const historicalData = Array.from({ length: points }, (_, i) => {
        const variance = (Math.random() - 0.5) * 0.1; // ±5% variance
        const date = new Date();

        if (period.includes('H')) {
          date.setHours(date.getHours() - (points - i));
        } else if (period.includes('D')) {
          date.setDate(date.getDate() - (points - i));
        } else if (period.includes('W')) {
          date.setDate(date.getDate() - (points - i) * 7);
        } else if (period.includes('M')) {
          date.setDate(date.getDate() - (points - i) * 30);
        } else if (period.includes('Y')) {
          date.setMonth(date.getMonth() - (points - i));
        }

        return {
          date: date.toISOString(),
          rate: baseRate * (1 + variance),
          volume: Math.floor(Math.random() * 50000) + 10000
        };
      });

      const currentRate = baseRate;
      const previousRate = historicalData[0]?.rate || baseRate;
      const changeValue = ((currentRate - previousRate) / previousRate * 100).toFixed(1);

      return {
        success: true,
        pair: `${from}/${to}`,
        period: period,
        currentRate: currentRate,
        change: {
          value: `${changeValue > 0 ? '+' : ''}${changeValue}%`,
          type: period
        },
        statistics: {
          low: Math.min(...historicalData.map(d => d.rate)),
          high: Math.max(...historicalData.map(d => d.rate)),
          close: currentRate
        },
        data: historicalData
      };
    },

    currencies: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Supported currencies requested');

      return {
        success: true,
        currencies: [
          { code: 'USD', name: 'US Dollar', country: 'US', symbol: '$' },
          { code: 'EUR', name: 'Euro', country: 'EU', symbol: '€' },
          { code: 'GBP', name: 'British Pound', country: 'GB', symbol: '£' },
          { code: 'RWF', name: 'Rwandan Franc', country: 'RW', symbol: 'FRw' },
          { code: 'GHS', name: 'Ghanaian Cedi', country: 'GH', symbol: '₵' },
          { code: 'KES', name: 'Kenyan Shilling', country: 'KE', symbol: 'KSh' },
          { code: 'UGX', name: 'Ugandan Shilling', country: 'UG', symbol: 'USh' },
          { code: 'TZS', name: 'Tanzanian Shilling', country: 'TZ', symbol: 'TSh' }
        ]
      };
    }
  },

  // Admin management endpoints
  adminManagement: {
    statistics: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin statistics requested');

      const totalAdmins = adminsData.length;
      const superAdmins = adminsData.filter(admin => admin.adminType === 'Super').length;
      const standardAdmins = adminsData.filter(admin => admin.adminType === 'Standard').length;

      return {
        super_admins: {
          count: superAdmins,
          percentage: `${Math.round((superAdmins / totalAdmins) * 100)}%`,
          country_distribution: {
            Rwanda: superAdmins,
            Ghana: 0,
            Kenya: 0
          }
        },
        standard_admins: {
          count: standardAdmins,
          percentage: `${Math.round((standardAdmins / totalAdmins) * 100)}%`,
          country_distribution: {
            Rwanda: standardAdmins,
            Ghana: 0,
            Kenya: 0
          }
        },
        total_admins: totalAdmins,
        active_admins: totalAdmins, // Assume all are active for testing
        inactive_admins: 0,
        success: true
      };
    },

    admins: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin list requested', params);

      const { page = 1, limit = 20, search = '', admin_type = '', status = '', phone_prefix = '' } = params;

      // Transform existing admin data to match expected format
      let processedAdmins = adminsData.map(admin => ({
        admin_id: admin.adminId,
        id: admin.adminId,
        full_name: admin.fullName,
        email: `${admin.fullName.toLowerCase().replace(/\s+/g, '.')}@inversepay.rw`,
        phone_number: admin.phoneNumber,
        admin_type: admin.adminType,
        status: 'Active', // Default status for testing
        avatar: admin.avatar,
        date_created: admin.dateCreated,
        time_created: admin.timeCreated,
        last_login: generateRandomDate('2025-01-01', '2025-01-15'),
        permissions: admin.adminType === 'Super' ? ['all'] : ['users', 'transactions', 'compliance'],
        created_by: 'System',
        branch: 'Kigali Head Office',
        gender: Math.random() > 0.5 ? 'Male' : 'Female',
        address: generateRandomAddress()
      }));

      // Apply search filter
      if (search) {
        processedAdmins = processedAdmins.filter(admin =>
          admin.full_name.toLowerCase().includes(search.toLowerCase()) ||
          admin.email.toLowerCase().includes(search.toLowerCase()) ||
          admin.admin_id.toLowerCase().includes(search.toLowerCase())
        );
      }

      // Apply admin type filter
      if (admin_type) {
        processedAdmins = processedAdmins.filter(admin =>
          admin.admin_type.toLowerCase() === admin_type.toLowerCase()
        );
      }

      // Apply status filter
      if (status) {
        processedAdmins = processedAdmins.filter(admin =>
          admin.status.toLowerCase() === status.toLowerCase()
        );
      }

      // Apply phone prefix filter
      if (phone_prefix) {
        processedAdmins = processedAdmins.filter(admin =>
          admin.phone_number.startsWith(phone_prefix)
        );
      }

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedAdmins = processedAdmins.slice(start, end);

      return {
        admins: paginatedAdmins,
        pagination: {
          current_page: parseInt(page),
          total_pages: Math.ceil(processedAdmins.length / limit),
          total_items: processedAdmins.length,
          items_per_page: parseInt(limit),
          has_next: end < processedAdmins.length,
          has_prev: page > 1
        },
        filters_applied: { search, admin_type, status, phone_prefix },
        success: true
      };
    },

    adminDetails: async (adminId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin details requested for:', adminId);
      if (isDevelopment()) console.log('🧪 Testing Mode: Available admin IDs:', adminsData.map(a => a.adminId));

      // Handle potential URL encoding/decoding issues
      const decodedAdminId = decodeURIComponent(adminId);
      if (isDevelopment()) console.log('🧪 Testing Mode: Decoded admin ID:', decodedAdminId);

      const admin = adminsData.find(a => a.adminId === adminId || a.adminId === decodedAdminId);
      if (!admin) {
        if (isDevelopment()) console.log('🧪 Testing Mode: Admin not found for ID:', adminId, 'or decoded:', decodedAdminId);
        throw new Error('Admin not found');
      }

      if (isDevelopment()) console.log('🧪 Testing Mode: Found admin:', admin.fullName);

      return {
        admin: {
          admin_id: admin.adminId,
          id: admin.adminId,
          full_name: admin.fullName,
          first_name: admin.fullName.split(' ')[0],
          last_name: admin.fullName.split(' ').slice(1).join(' '),
          email: `${admin.fullName.toLowerCase().replace(/\s+/g, '.')}@inversepay.rw`,
          phone_number: admin.phoneNumber,
          admin_type: admin.adminType,
          status: 'Active',
          avatar: admin.avatar,
          date_created: admin.dateCreated,
          time_created: admin.timeCreated,
          last_login: generateRandomDate('2025-01-01', '2025-01-15'),
          permissions: admin.adminType === 'Super' ? ['all'] : ['users', 'transactions', 'compliance'],
          created_by: 'System Admin',
          branch: 'Kigali Head Office',
          gender: Math.random() > 0.5 ? 'Male' : 'Female',
          address: generateRandomAddress(),
          total_logins: Math.floor(Math.random() * 500) + 50,
          failed_login_attempts: Math.floor(Math.random() * 5),
          last_password_change: generateRandomDate('2024-06-01', '2024-12-31'),
          two_factor_enabled: Math.random() > 0.3,
          account_locked: false,
          timezone: 'Africa/Kigali',
          language: 'English',
          notification_preferences: {
            email: true,
            sms: true,
            in_app: true
          }
        },
        success: true
      };
    },

    adminActivity: async (adminId, params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin activity requested for:', adminId, params);

      const { page = 1, limit = 10, search = '', date_from = '', date_to = '' } = params;

      const activityTypes = [
        'Login', 'Logout', 'User Created', 'User Updated', 'User Suspended',
        'Transaction Reviewed', 'Compliance Check', 'Admin Created', 'Settings Updated',
        'Report Generated', 'Permission Changed', 'Password Changed'
      ];

      const statuses = ['Success', 'Failed', 'Warning'];

      // Generate activity data
      const allActivities = Array.from({ length: 100 }, (_, i) => {
        const activity = {
          id: `ACT-${Date.now()}-${i.toString().padStart(3, '0')}`,
          admin_id: adminId,
          activity_type: activityTypes[Math.floor(Math.random() * activityTypes.length)],
          description: `Admin performed ${activityTypes[Math.floor(Math.random() * activityTypes.length)].toLowerCase()} action`,
          status: statuses[Math.floor(Math.random() * statuses.length)],
          ip_address: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          user_agent: Math.random() > 0.5 ? 'Chrome/91.0.4472.124' : 'Safari/14.1.1',
          location: ['Kigali, Rwanda', 'Musanze, Rwanda', 'Huye, Rwanda'][Math.floor(Math.random() * 3)],
          timestamp: generateRandomDate('2024-01-01', '2025-01-15'),
          details: {
            affected_resource: Math.random() > 0.5 ? `User ${Math.floor(Math.random() * 1000)}` : 'System Setting',
            changes_made: ['Status Updated', 'Profile Modified', 'Permission Granted'][Math.floor(Math.random() * 3)]
          }
        };
        return activity;
      });

      // Apply filters
      let filteredActivities = allActivities;

      if (search) {
        filteredActivities = filteredActivities.filter(activity =>
          activity.activity_type.toLowerCase().includes(search.toLowerCase()) ||
          activity.description.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (date_from) {
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.timestamp) >= new Date(date_from)
        );
      }

      if (date_to) {
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.timestamp) <= new Date(date_to)
        );
      }

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedActivities = filteredActivities.slice(start, end);

      return {
        activities: paginatedActivities,
        pagination: {
          current_page: parseInt(page),
          total_pages: Math.ceil(filteredActivities.length / limit),
          total_items: filteredActivities.length,
          items_per_page: parseInt(limit),
          has_next: end < filteredActivities.length,
          has_prev: page > 1
        },
        summary: {
          total_activities: filteredActivities.length,
          success_count: filteredActivities.filter(a => a.status === 'Success').length,
          failed_count: filteredActivities.filter(a => a.status === 'Failed').length,
          warning_count: filteredActivities.filter(a => a.status === 'Warning').length
        },
        filters_applied: { search, date_from, date_to },
        success: true
      };
    },

    adminSessions: async (adminId, params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Admin sessions requested for:', adminId, params);

      const { page = 1, limit = 10, active_only = false } = params;

      // Generate session data
      const allSessions = Array.from({ length: 25 }, (_, i) => {
        const isActive = Math.random() > 0.7;
        const session = {
          id: `SES-${Date.now()}-${i.toString().padStart(3, '0')}`,
          admin_id: adminId,
          session_token: `tk_${Math.random().toString(36).substring(2, 15)}`,
          ip_address: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          user_agent: [
            'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
            'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36'
          ][Math.floor(Math.random() * 3)],
          device_type: ['Desktop', 'Mobile', 'Tablet'][Math.floor(Math.random() * 3)],
          location: ['Kigali, Rwanda', 'Musanze, Rwanda', 'Huye, Rwanda'][Math.floor(Math.random() * 3)],
          is_active: isActive,
          created_at: generateRandomDate('2024-12-01', '2025-01-15'),
          last_activity: isActive ? generateRandomDate('2025-01-14', '2025-01-15') : generateRandomDate('2024-12-01', '2025-01-10'),
          expires_at: isActive ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : generateRandomDate('2024-12-01', '2025-01-10'),
          login_method: ['Password', 'OTP', 'SSO'][Math.floor(Math.random() * 3)]
        };
        return session;
      });

      // Apply filters
      let filteredSessions = allSessions;

      if (active_only) {
        filteredSessions = filteredSessions.filter(session => session.is_active);
      }

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedSessions = filteredSessions.slice(start, end);

      return {
        sessions: paginatedSessions,
        pagination: {
          current_page: parseInt(page),
          total_pages: Math.ceil(filteredSessions.length / limit),
          total_items: filteredSessions.length,
          items_per_page: parseInt(limit),
          has_next: end < filteredSessions.length,
          has_prev: page > 1
        },
        summary: {
          total_sessions: filteredSessions.length,
          active_sessions: filteredSessions.filter(s => s.is_active).length,
          expired_sessions: filteredSessions.filter(s => !s.is_active).length
        },
        filters_applied: { active_only },
        success: true
      };
    },

    getCurrentAdmin: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Current admin profile requested');

      // Return the first super admin as the "current" admin for testing
      const currentAdmin = adminsData.find(admin => admin.adminType === 'Super') || adminsData[0];

      if (!currentAdmin) {
        throw new Error('No admin found');
      }

      return {
        admin: {
          id: currentAdmin.adminId,
          admin_id: currentAdmin.adminId,
          full_name: currentAdmin.fullName,
          first_name: currentAdmin.fullName.split(' ')[0],
          last_name: currentAdmin.fullName.split(' ').slice(1).join(' '),
          email: `${currentAdmin.fullName.toLowerCase().replace(/\s+/g, '.')}@inversepay.rw`,
          phone_number: currentAdmin.phoneNumber,
          admin_type: currentAdmin.adminType,
          status: 'Active',
          avatar: currentAdmin.avatar,
          date_created: currentAdmin.dateCreated,
          time_created: currentAdmin.timeCreated,
          last_login: new Date().toISOString(),
          permissions: currentAdmin.adminType === 'Super' ? ['all'] : ['users', 'transactions', 'compliance'],
          branch: 'Kigali Head Office',
          timezone: 'Africa/Kigali',
          language: 'English',
          is_current_user: true
        },
        success: true
      };
    },

    createAdmin: async (adminData) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Creating new admin', adminData);

      // Simulate longer delay for admin creation (realistic processing time)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate a new admin ID
      const newAdminId = `INV${Math.floor(Math.random() * 100000000).toString().padStart(8, '0')}`;

      // Create the new admin object
      const newAdmin = {
        admin_id: newAdminId,
        id: newAdminId,
        full_name: adminData.full_name || `${adminData.first_name} ${adminData.last_name}`,
        first_name: adminData.first_name,
        last_name: adminData.last_name,
        email: adminData.email,
        phone_number: adminData.phone_number,
        admin_type: adminData.admin_type,
        status: 'Active',
        avatar: '/assets/images/admin_placeholder_img.png',
        date_created: new Date().toLocaleDateString('en-GB'),
        time_created: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        last_login: null,
        permissions: adminData.admin_type === 'Super' ? ['all'] : ['users', 'transactions', 'compliance'],
        created_by: 'System Admin',
        branch: adminData.branch || 'Kigali Head Office',
        gender: adminData.gender,
        address: adminData.address,
        invitation_sent: adminData.send_welcome_email || false,
        invitation_sent_at: adminData.send_welcome_email ? new Date().toISOString() : null,
        account_setup_completed: false
      };

      return {
        admin: newAdmin,
        invitation: {
          sent: adminData.send_welcome_email || false,
          email: adminData.email,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          setup_url: `https://admin.inversepay.rw/setup?token=${Math.random().toString(36).substring(2, 15)}`
        },
        message: `Admin profile created successfully. ${adminData.send_welcome_email ? 'Welcome email sent to ' + adminData.email : 'No welcome email sent.'}`,
        success: true
      };
    },

    // Legacy function for backward compatibility
    getAll: async (params = {}) => {
      await simulateDelay();
      const { page = 1, limit = 10 } = params;

      const start = (page - 1) * limit;
      const end = start + limit;

      return {
        admins: adminsData.slice(start, end),
        total: adminsData.length,
        page: parseInt(page),
        limit: parseInt(limit),
        total_pages: Math.ceil(adminsData.length / limit)
      };
    }
  },

  // Compliance endpoints
  compliance: {
    getKycStatistics: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: KYC statistics requested');

      return {
        kyc_verified: {
          count: 23456,
          percentage: '91.2%',
          trend: 'up',
          change: '+2.3%'
        },
        kyc_unverified: {
          count: 2391,
          percentage: '8.8%',
          trend: 'down',
          change: '-1.2%'
        },
        soon_to_expire: {
          count: 156,
          percentage: '0.6%',
          trend: 'stable',
          change: '0.0%'
        },
        total_users: 25847,
        success: true
      };
    },

    getAmlRiskDistribution: async () => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: AML risk distribution requested');

      return {
        risk_distribution: [
          { name: 'Low Risk', value: 75, count: 19385, color: '#10B981' },
          { name: 'Medium Risk', value: 20, count: 5169, color: '#F59E0B' },
          { name: 'High Risk', value: 5, count: 1293, color: '#EF4444' }
        ],
        total_users: 25847,
        last_updated: new Date().toISOString(),
        success: true
      };
    },

    getAmlFlaggedTimeline: async (timeFilter = 'Monthly') => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: AML flagged timeline requested', { timeFilter });

      let timelineData = [];

      switch (timeFilter) {
        case 'Daily':
          timelineData = Array.from({ length: 30 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (29 - i));
            return {
              period: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              date: date.toISOString().split('T')[0],
              flagged_users: Math.floor(Math.random() * 15) + 5,
              total_checks: Math.floor(Math.random() * 150) + 50
            };
          });
          break;

        case 'Weekly':
          timelineData = Array.from({ length: 12 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (11 - i) * 7);
            return {
              period: `Week ${12 - i}`,
              date: date.toISOString().split('T')[0],
              flagged_users: Math.floor(Math.random() * 50) + 20,
              total_checks: Math.floor(Math.random() * 500) + 200
            };
          });
          break;

        case 'Yearly':
          timelineData = Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() - (4 - i);
            return {
              period: year.toString(),
              date: `${year}-01-01`,
              flagged_users: Math.floor(Math.random() * 500) + 200,
              total_checks: Math.floor(Math.random() * 5000) + 2000
            };
          });
          break;

        default: // Monthly
          timelineData = [
            { period: 'Jan', date: '2024-01-01', flagged_users: 45, total_checks: 1203 },
            { period: 'Feb', date: '2024-02-01', flagged_users: 52, total_checks: 1156 },
            { period: 'Mar', date: '2024-03-01', flagged_users: 38, total_checks: 1089 },
            { period: 'Apr', date: '2024-04-01', flagged_users: 61, total_checks: 1234 },
            { period: 'May', date: '2024-05-01', flagged_users: 43, total_checks: 1187 },
            { period: 'Jun', date: '2024-06-01', flagged_users: 55, total_checks: 1298 },
            { period: 'Jul', date: '2024-07-01', flagged_users: 49, total_checks: 1265 },
            { period: 'Aug', date: '2024-08-01', flagged_users: 67, total_checks: 1321 },
            { period: 'Sep', date: '2024-09-01', flagged_users: 58, total_checks: 1289 },
            { period: 'Oct', date: '2024-10-01', flagged_users: 72, total_checks: 1356 },
            { period: 'Nov', date: '2024-11-01', flagged_users: 64, total_checks: 1298 },
            { period: 'Dec', date: '2024-12-01', flagged_users: 59, total_checks: 1276 }
          ];
      }

      return {
        data: timelineData,
        timeFilter,
        summary: {
          total_flagged: timelineData.reduce((sum, item) => sum + item.flagged_users, 0),
          total_checks: timelineData.reduce((sum, item) => sum + item.total_checks, 0),
          average_flag_rate: (timelineData.reduce((sum, item) => sum + (item.flagged_users / item.total_checks), 0) / timelineData.length * 100).toFixed(2)
        },
        success: true
      };
    },

    getAmlMonitoringUsers: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: AML monitoring users requested', params);

      const { page = 1, limit = 10, search = '', riskLevel = '' } = params;

      const rwandanNames = [
        'Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana',
        'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine', 'Bob Hakizimana',
        'Emma Uwamahoro', 'Frank Bizimana', 'Helen Uwineza', 'Paul Nsabimana',
        'Sarah Mukamana', 'Joseph Nkurunziza', 'Agnes Uwimpuhwe', 'Vincent Habimana'
      ];

      const riskLevels = ['Low', 'Medium', 'High'];
      const statuses = ['Active', 'Under Review', 'Flagged', 'Cleared'];
      const locations = ['Kigali', 'Musanze', 'Huye', 'Muhanga', 'Karongi'];

      // Generate comprehensive user data
      const allUsers = Array.from({ length: 50 }, (_, i) => {
        const risk = riskLevels[Math.floor(Math.random() * riskLevels.length)];
        const status = statuses[Math.floor(Math.random() * statuses.length)];
        const user = {
          id: `USER-${(i + 1).toString().padStart(4, '0')}`,
          name: rwandanNames[Math.floor(Math.random() * rwandanNames.length)],
          email: `user${i + 1}@example.com`,
          phone: `+250 ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100}`,
          risk_level: risk,
          risk_score: risk === 'High' ? Math.floor(Math.random() * 30) + 70 :
            risk === 'Medium' ? Math.floor(Math.random() * 40) + 30 :
              Math.floor(Math.random() * 30) + 1,
          status: status,
          location: locations[Math.floor(Math.random() * locations.length)],
          account_balance: Math.floor(Math.random() * 5000000) + 50000,
          currency: Math.random() > 0.3 ? 'RWF' : 'GHS',
          last_transaction: generateRandomDate('2024-12-01', '2025-01-15'),
          flagged_transactions: Math.floor(Math.random() * 10),
          total_transactions: Math.floor(Math.random() * 500) + 50,
          kyc_status: Math.random() > 0.2 ? 'Verified' : 'Pending',
          created_at: generateRandomDate('2023-01-01', '2024-12-31'),
          last_activity: generateRandomDate('2024-12-01', '2025-01-15')
        };
        return user;
      });

      // Apply filters
      let filteredUsers = allUsers;

      if (search) {
        filteredUsers = filteredUsers.filter(user =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase()) ||
          user.id.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (riskLevel) {
        filteredUsers = filteredUsers.filter(user => user.risk_level === riskLevel);
      }

      // Pagination
      const totalItems = filteredUsers.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);
      const paginatedUsers = filteredUsers.slice(start, end);

      return {
        users: paginatedUsers,
        pagination: {
          current_page: validPage,
          total_pages: totalPages,
          total_items: totalItems,
          items_per_page: parseInt(limit),
          has_next: validPage < totalPages,
          has_prev: validPage > 1
        },
        filters_applied: { search, riskLevel },
        success: true
      };
    },

    getSuspiciousTransactions: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Suspicious transactions requested', params);

      const { page = 1, limit = 20, search = '', severity = '', status = '', dateFrom = '', dateTo = '' } = params;

      const severityLevels = ['Low', 'Medium', 'High', 'Critical'];
      const statusOptions = ['Pending', 'Under Review', 'Resolved', 'Escalated'];
      const transactionTypes = ['Transfer', 'Withdrawal', 'Deposit', 'Payment'];
      const rwandanNames = [
        'Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana',
        'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine'
      ];

      // Generate suspicious transactions
      const allTransactions = Array.from({ length: 100 }, (_, i) => {
        const txnSeverity = severityLevels[Math.floor(Math.random() * severityLevels.length)];
        const txnStatus = statusOptions[Math.floor(Math.random() * statusOptions.length)];
        const txnType = transactionTypes[Math.floor(Math.random() * transactionTypes.length)];
        const amount = Math.floor(Math.random() * 10000000) + 100000; // Large amounts for suspicion

        return {
          id: `SAR-${(i + 1).toString().padStart(6, '0')}`,
          transaction_id: `TXN-${Date.now()}-${i.toString().padStart(3, '0')}`,
          user_name: rwandanNames[Math.floor(Math.random() * rwandanNames.length)],
          user_id: `USER-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`,
          amount: amount,
          currency: Math.random() > 0.2 ? 'RWF' : 'GHS',
          type: txnType,
          severity: txnSeverity,
          status: txnStatus,
          flags: [
            'High Amount',
            'Unusual Pattern',
            'Multiple Transactions',
            'New User',
            'Cross-Border'
          ].filter(() => Math.random() > 0.6),
          description: `Suspicious ${txnType.toLowerCase()} transaction flagged for review`,
          created_at: generateRandomDate('2024-10-01', '2025-01-15'),
          updated_at: generateRandomDate('2024-10-01', '2025-01-15'),
          assigned_to: Math.random() > 0.3 ? 'Admin Sarah Mukamana' : null,
          priority: txnSeverity === 'Critical' ? 'Urgent' :
            txnSeverity === 'High' ? 'High' : 'Normal'
        };
      });

      // Apply filters
      let filteredTransactions = allTransactions;

      if (search) {
        filteredTransactions = filteredTransactions.filter(txn =>
          txn.user_name.toLowerCase().includes(search.toLowerCase()) ||
          txn.transaction_id.toLowerCase().includes(search.toLowerCase()) ||
          txn.id.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (severity) {
        filteredTransactions = filteredTransactions.filter(txn => txn.severity === severity);
      }

      if (status) {
        filteredTransactions = filteredTransactions.filter(txn => txn.status === status);
      }

      if (dateFrom) {
        filteredTransactions = filteredTransactions.filter(txn =>
          new Date(txn.created_at) >= new Date(dateFrom)
        );
      }

      if (dateTo) {
        filteredTransactions = filteredTransactions.filter(txn =>
          new Date(txn.created_at) <= new Date(dateTo)
        );
      }

      // Pagination
      const totalItems = filteredTransactions.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / limit));
      const validPage = Math.max(1, Math.min(page, totalPages)); // Ensure page is within bounds
      
      const start = (validPage - 1) * limit;
      const end = Math.min(start + limit, totalItems);
      const paginatedTransactions = filteredTransactions.slice(start, end);

      return {
        transactions: paginatedTransactions,
        pagination: {
          current_page: validPage,
          total_pages: totalPages,
          total_items: totalItems,
          items_per_page: parseInt(limit),
          has_next: validPage < totalPages,
          has_prev: validPage > 1
        },
        summary: {
          total_suspicious: filteredTransactions.length,
          by_severity: {
            critical: filteredTransactions.filter(t => t.severity === 'Critical').length,
            high: filteredTransactions.filter(t => t.severity === 'High').length,
            medium: filteredTransactions.filter(t => t.severity === 'Medium').length,
            low: filteredTransactions.filter(t => t.severity === 'Low').length
          },
          by_status: {
            pending: filteredTransactions.filter(t => t.status === 'Pending').length,
            under_review: filteredTransactions.filter(t => t.status === 'Under Review').length,
            resolved: filteredTransactions.filter(t => t.status === 'Resolved').length,
            escalated: filteredTransactions.filter(t => t.status === 'Escalated').length
          }
        },
        filters_applied: { search, severity, status, dateFrom, dateTo },
        success: true
      };
    },

    getSuspiciousLoginAttempts: async (params = {}) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Suspicious login attempts requested', params);

      const { page = 1, limit = 20, search = '', severity = '', status = '' } = params;

      const severityLevels = ['Low', 'Medium', 'High', 'Critical'];
      const statusOptions = ['Active', 'Blocked', 'Monitoring', 'Resolved'];
      const rwandanNames = [
        'Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana',
        'Grace Uwimana', 'David Niyonzima', 'Alice Mukasine'
      ];

      // Generate suspicious login attempts
      const allAttempts = Array.from({ length: 75 }, (_, i) => {
        const attemptSeverity = severityLevels[Math.floor(Math.random() * severityLevels.length)];
        const attemptStatus = statusOptions[Math.floor(Math.random() * statusOptions.length)];

        return {
          id: `LOGIN-${(i + 1).toString().padStart(6, '0')}`,
          user_name: rwandanNames[Math.floor(Math.random() * rwandanNames.length)],
          user_id: `USER-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`,
          ip_address: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          location: ['Kigali, Rwanda', 'Lagos, Nigeria', 'Unknown', 'Kampala, Uganda', 'Nairobi, Kenya'][Math.floor(Math.random() * 5)],
          attempts_count: Math.floor(Math.random() * 20) + 1,
          time_window: `${Math.floor(Math.random() * 60) + 1} minutes`,
          severity: attemptSeverity,
          status: attemptStatus,
          flags: [
            'Multiple Failed Attempts',
            'Unusual Location',
            'Different Device',
            'Unusual Time',
            'VPN/Proxy Detected'
          ].filter(() => Math.random() > 0.5),
          first_attempt: generateRandomDate('2024-12-01', '2025-01-15'),
          last_attempt: generateRandomDate('2024-12-01', '2025-01-15'),
          device_info: Math.random() > 0.5 ? 'Mobile (Android)' : 'Desktop (Windows)',
          assigned_to: Math.random() > 0.4 ? 'Admin Peter Nshimiyimana' : null
        };
      });

      // Apply filters
      let filteredAttempts = allAttempts;

      if (search) {
        filteredAttempts = filteredAttempts.filter(attempt =>
          attempt.user_name.toLowerCase().includes(search.toLowerCase()) ||
          attempt.ip_address.includes(search) ||
          attempt.id.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (severity) {
        filteredAttempts = filteredAttempts.filter(attempt => attempt.severity === severity);
      }

      if (status) {
        filteredAttempts = filteredAttempts.filter(attempt => attempt.status === status);
      }

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedAttempts = filteredAttempts.slice(start, end);

      return {
        login_attempts: paginatedAttempts,
        pagination: {
          current_page: parseInt(page),
          total_pages: Math.ceil(filteredAttempts.length / limit),
          total_items: filteredAttempts.length,
          items_per_page: parseInt(limit),
          has_next: end < filteredAttempts.length,
          has_prev: page > 1
        },
        summary: {
          total_suspicious: filteredAttempts.length,
          by_severity: {
            critical: filteredAttempts.filter(a => a.severity === 'Critical').length,
            high: filteredAttempts.filter(a => a.severity === 'High').length,
            medium: filteredAttempts.filter(a => a.severity === 'Medium').length,
            low: filteredAttempts.filter(a => a.severity === 'Low').length
          },
          by_status: {
            active: filteredAttempts.filter(a => a.status === 'Active').length,
            blocked: filteredAttempts.filter(a => a.status === 'Blocked').length,
            monitoring: filteredAttempts.filter(a => a.status === 'Monitoring').length,
            resolved: filteredAttempts.filter(a => a.status === 'Resolved').length
          }
        },
        filters_applied: { search, severity, status },
        success: true
      };
    },

    getSarTransactionDetails: async (transactionId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: SAR transaction details requested for:', transactionId);

      const rwandanNames = ['Jean Baptiste Uwimana', 'Marie Claire Mukamana', 'Eric Nshimiyimana'];
      const selectedUser = rwandanNames[Math.floor(Math.random() * rwandanNames.length)];

      return {
        transaction: {
          id: transactionId,
          user: {
            name: selectedUser,
            id: `USER-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`,
            email: `${selectedUser.toLowerCase().replace(/ /g, '.')}@example.com`,
            phone: `+250 ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100}`,
            kyc_status: Math.random() > 0.3 ? 'Verified' : 'Pending',
            risk_level: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)]
          },
          amount: Math.floor(Math.random() * 5000000) + 500000,
          currency: Math.random() > 0.2 ? 'RWF' : 'GHS',
          type: ['Transfer', 'Withdrawal', 'Deposit'][Math.floor(Math.random() * 3)],
          status: ['Pending', 'Under Review', 'Flagged'][Math.floor(Math.random() * 3)],
          severity: ['Medium', 'High', 'Critical'][Math.floor(Math.random() * 3)],
          flags: [
            'High Amount Transaction',
            'Unusual Transaction Pattern',
            'Multiple Large Transactions',
            'Cross-Border Transfer',
            'New Account Activity'
          ].filter(() => Math.random() > 0.4),
          created_at: generateRandomDate('2024-12-01', '2025-01-15'),
          description: 'Large transaction flagged for manual review due to unusual pattern and amount',
          recipient: {
            name: 'External Bank Account',
            account: `ACC-${Math.floor(Math.random() * 1000000)}`,
            bank: Math.random() > 0.5 ? 'Bank of Kigali' : 'Equity Bank Rwanda'
          },
          investigation: {
            assigned_to: 'Admin Sarah Mukamana',
            assigned_at: generateRandomDate('2024-12-01', '2025-01-15'),
            notes: [
              {
                author: 'Admin Sarah Mukamana',
                timestamp: generateRandomDate('2024-12-01', '2025-01-15'),
                content: 'Initial review - transaction amount exceeds normal user pattern. Investigating source of funds.'
              },
              {
                author: 'Admin Peter Nshimiyimana',
                timestamp: generateRandomDate('2024-12-01', '2025-01-15'),
                content: 'Customer contacted for additional documentation. Waiting for response.'
              }
            ],
            status: 'Under Investigation',
            priority: 'High'
          }
        },
        related_transactions: Array.from({ length: 5 }, (_, i) => ({
          id: `TXN-${Date.now() + i}-${i.toString().padStart(3, '0')}`,
          amount: Math.floor(Math.random() * 1000000) + 50000,
          currency: 'RWF',
          type: ['Transfer', 'Deposit', 'Withdrawal'][Math.floor(Math.random() * 3)],
          date: generateRandomDate('2024-11-01', '2024-12-31'),
          status: ['Completed', 'Pending'][Math.floor(Math.random() * 2)]
        })),
        success: true
      };
    },

    runAmlCheck: async (userId) => {
      await simulateDelay();
      if (isDevelopment()) console.log('🧪 Testing Mode: Running AML check for user:', userId);

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      const riskLevel = ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)];
      const riskScore = riskLevel === 'High' ? Math.floor(Math.random() * 30) + 70 :
        riskLevel === 'Medium' ? Math.floor(Math.random() * 40) + 30 :
          Math.floor(Math.random() * 30) + 1;

      return {
        user_id: userId,
        check_id: `AML-CHECK-${Date.now()}`,
        status: 'Completed',
        risk_level: riskLevel,
        risk_score: riskScore,
        flags: [
          'Transaction Pattern Analysis',
          'Geographic Risk Assessment',
          'PEP Screening',
          'Sanctions List Check',
          'Adverse Media Check'
        ].filter(() => Math.random() > 0.6),
        recommendations: riskLevel === 'High' ?
          ['Enhanced Due Diligence Required', 'Manual Review Recommended', 'Consider Transaction Limits'] :
          riskLevel === 'Medium' ?
            ['Standard Monitoring', 'Periodic Review'] :
            ['Standard Processing', 'Routine Monitoring'],
        completed_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days
        success: true
      };
    },

    // Deprecated - keeping for backward compatibility
    getKycAmlData: async () => {
      await simulateDelay();

      return {
        kycStatistics: {
          verified_users: 23456,
          unverified_users: 2391,
          soon_to_be_outdated: 156
        },
        amlRiskDistribution: {
          low_risk: 75,
          medium_risk: 20,
          high_risk: 5
        },
        amlFlaggedTimeline: [
          { month: 'Jan', flagged_users: 45 },
          { month: 'Feb', flagged_users: 52 },
          { month: 'Mar', flagged_users: 38 },
          { month: 'Apr', flagged_users: 61 },
          { month: 'May', flagged_users: 43 },
          { month: 'Jun', flagged_users: 55 }
        ]
      };
    }
  }
};

export default mockDataService;

// Additional functions for complete mock service
export const additionalMockFunctions = {
  userTransactions: async (userId, params = {}) => {
    const { page = 1, limit = 9 } = params;
    if (isDevelopment()) console.log('🧪 Testing Mode: User transactions requested for ID:', userId);

    const transactionTypes = ['transfer', 'deposit', 'withdrawal', 'payment', 'refund'];
    const statuses = ['completed', 'pending', 'failed'];
    const currencies = ['RWF', 'GHS', 'USD'];

    const userTransactions = Array.from({ length: 50 }, (_, i) => ({
      id: `TXN-${Date.now()}-${i.toString().padStart(3, '0')}`,
      user_id: userId,
      type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
      amount: Math.floor(Math.random() * 100000) + 100,
      currency: currencies[Math.floor(Math.random() * currencies.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      date: generateRandomDate('2024-01-01', '2025-01-15'),
      description: `Transaction for user ${userId}`,
      reference: `REF-${Date.now()}-${i}`,
      fee: Math.floor(Math.random() * 1000) + 10,
      recipient: Math.random() > 0.5 ? 'External Account' : 'Internal Transfer',
      created_at: generateRandomDate('2024-01-01', '2025-01-15')
    }));

    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      transactions: userTransactions.slice(start, end),
      total: userTransactions.length,
      page: parseInt(page),
      limit: parseInt(limit),
      total_pages: Math.ceil(userTransactions.length / limit),
      pagination: {
        total: userTransactions.length,
        page: parseInt(page),
        limit: parseInt(limit),
        total_pages: Math.ceil(userTransactions.length / limit)
      }
    };
  }
};

// Extend mockDataService with missing functions
mockDataService.dashboard.userTransactions = additionalMockFunctions.userTransactions;
mockDataService.dashboard.userSecurityData = async (userId) => {
  if (isDevelopment()) console.log('🧪 Testing Mode: User security data requested for ID:', userId);
  return {
    user_id: userId,
    last_login: generateRandomDate('2025-01-01', '2025-01-15'),
    login_attempts: Math.floor(Math.random() * 10) + 1,
    failed_login_attempts: Math.floor(Math.random() * 3),
    password_last_changed: generateRandomDate('2024-06-01', '2024-12-31'),
    two_factor_enabled: Math.random() > 0.4,
    location_history: []
  };
};
mockDataService.dashboard.userDocuments = async (userId) => {
  if (isDevelopment()) console.log('🧪 Testing Mode: User documents requested for ID:', userId);
  return {
    user_id: userId,
    documents: []
  };
}; 